import { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useNav } from '../hooks/useNav'
import { supabase } from '../lib/supabase'
import { toSlug } from '../lib/slug'
import { useAuth } from '../context/AuthContext'
import { useExchangeRate } from '../hooks/useExchangeRate'
import { useCurrency } from '../context/CurrencyContext'
import Topbar from '../components/Topbar'
import Footer from '../components/Footer'
import WATCH_REFS from '../data/watchRefs'

const CATEGORIES = ['Watches', 'Jewellery', 'Bags']

const CONDITIONS = [
  'pre-owned conditions with MINOR signs of usage',
  'pre-owned conditions with MAJOR signs of usage',
  'Fair',
  'Needs Repair',
  'Repaired',
  'Repaired Albania',
  'Pre-owned',
]

const BRANDS = [
  'A. Lange & Söhne','Audemars Piguet','Balenciaga','Blancpain','Bottega Veneta',
  'Breguet','Breitling','Bulgari','Cartier','Celine','Chanel','Chopard','De Beers',
  'Dior','Fendi','Girard-Perregaux','Graff','Grand Seiko','Gucci','Harry Winston',
  'Hermès','Hublot','IWC','Jaeger-LeCoultre','Loewe','Louis Vuitton','Mikimoto',
  'Omega','Other','Panerai','Patek Philippe','Piaget','Prada','Richard Mille','Rolex',
  'Saint Laurent','TAG Heuer','Tiffany & Co','Tudor','Ulysse Nardin','Vacheron Constantin',
  'Van Cleef & Arpels','Zenith'
]

const JEWELLERY_BRANDS = new Set([
  'balenciaga','bottega veneta','bulgari','cartier','celine','chanel','de beers',
  'dior','fendi','gucci','hermès','hermes','loewe','louis vuitton','mikimoto',
  'prada','saint laurent','tiffany & co','van cleef & arpels','harry winston','graff','chopard','piaget',
])

const BAG_BRANDS = new Set([
  'balenciaga','bottega veneta','celine','chanel','dior','fendi','gucci',
  'hermès','hermes','loewe','louis vuitton','prada','saint laurent',
])

const BAG_CONDITIONS = [
  'Excellent',
  'Very Good',
  'Good',
  'Preowned',
  'Preowned/Excellent condition',
]

const BRAND_MAP = {
  'bvlgari': 'Bulgari', 'bulgari': 'Bulgari',
  'van cleef': 'Van Cleef & Arpels', 'vca': 'Van Cleef & Arpels',
  'cartier': 'Cartier', 'chanel': 'Chanel', 'chopard': 'Chopard',
  'hermes': 'Hermès',
  'louis vuitton': 'Louis Vuitton', 'lv': 'Louis Vuitton',
  'gucci': 'Gucci', 'prada': 'Prada',
  'christian dior': 'Dior', 'dior': 'Dior',
  'tiffany': 'Tiffany & Co', 'tiffany & co': 'Tiffany & Co',
  'harry winston': 'Harry Winston', 'graff': 'Graff',
  'fendi': 'Fendi', 'bottega veneta': 'Bottega Veneta',
  'saint laurent': 'Saint Laurent', 'ysl': 'Saint Laurent',
  'balenciaga': 'Balenciaga', 'loewe': 'Loewe',
  'celine': 'Celine', 'burberry': 'Burberry',
  'valentino': 'Valentino', 'chloe': 'Chloé',
  'jacquemus': 'Jacquemus', 'dolce & gabbana': 'Dolce & Gabbana',
  'dolce': 'Dolce & Gabbana', 'givenchy': 'Givenchy',
  'alexander mcqueen': 'Alexander McQueen', 'mcm': 'MCM',
  'coach': 'Coach', 'mulberry': 'Mulberry', 'furla': 'Furla',
  'michael kors': 'Michael Kors', 'versace': 'Versace',
  'miu miu': 'Miu Miu', 'marc jacobs': 'Marc Jacobs',
}

function normalizeForBrandMatch(value = '') {
  return Array.from(value.normalize('NFD'))
    .filter(ch => { const code = ch.codePointAt(0); return code < 0x0300 || code > 0x036f })
    .join('')
    .toLowerCase()
}

function findWholeAlias(text, alias) {
  let from = 0
  while (from <= text.length - alias.length) {
    const index = text.indexOf(alias, from)
    if (index === -1) return null
    const before = text[index - 1]
    const after = text[index + alias.length]
    const startsClean = !before || !/[a-z0-9]/i.test(before)
    const endsClean = !after || !/[a-z0-9]/i.test(after)
    if (startsClean && endsClean) return { index, length: alias.length }
    from = index + 1
  }
  return null
}

function detectBrand(text) {
  const normalizedText = normalizeForBrandMatch(text)
  const aliases = [
    ...Object.entries(BRAND_MAP),
    ...BRANDS.map(brand => [normalizeForBrandMatch(brand), brand]),
  ].sort(([a], [b]) => b.length - a.length)

  for (const [rawAlias, canonical] of aliases) {
    const alias = normalizeForBrandMatch(rawAlias)
    const match = findWholeAlias(normalizedText, alias)
    if (match) return { brand: canonical, ...match }
  }
  return null
}

const BAG_FORM_BRANDS = [...new Set([...BRANDS, ...Object.values(BRAND_MAP)])].sort()

function parseBagPriceLine(line = '') {
  const match = line.match(/-?[\d]['\d,.\s]*[\d]|-?\d+/)
  if (!match) return ''
  const raw = match[0].replace(/['\s]/g, '')
  const dots = (raw.match(/\./g) || []).length
  const commas = (raw.match(/,/g) || []).length
  let normalized = raw
  if (dots > 1) normalized = raw.replace(/\./g, '')
  else if (commas > 1) normalized = raw.replace(/,/g, '')
  else if (dots === 1 && commas > 0) normalized = raw.replace(/[.,]/g, '')
  else if (commas === 1 && raw.indexOf(',') > raw.length - 4) normalized = raw.replace(',', '.')
  else normalized = raw.replace(/,/g, '')
  const value = Number(normalized)
  return Number.isFinite(value) ? String(Math.max(0, Math.round(value))) : ''
}

function detectPriceCurrency(line = '') {
  if (/\b(?:cny|rmb|yuan)\b|¥|￥/i.test(line)) return 'CNY'
  if (/\b(?:usd|dollar|dollars)\b|\$/i.test(line)) return 'USD'
  if (/\b(?:eur|euro|euros)\b|€/i.test(line)) return 'EUR'
  return null
}

function parseBagPriceInput(line = '') {
  return {
    amount: parseBagPriceLine(line),
    currency: detectPriceCurrency(line),
  }
}

function detectBagCondition(text = '') {
  if (/pre[- ]?owned\s*\/\s*excellent\s+condition/i.test(text)) return 'Preowned/Excellent condition'
  if (/\bvery\s+good\b/i.test(text)) return 'Very Good'
  if (/\bexcellent\b/i.test(text)) return 'Excellent'
  if (/\bgood\b/i.test(text)) return 'Good'
  if (/\bpre[- ]?owned\b/i.test(text)) return 'Preowned'
  return null
}

function stripBagCondition(value = '') {
  return value
    .replace(/\b(?:pre[- ]?owned\s*\/\s*excellent\s+condition|very\s+good|excellent|good|pre[- ]?owned)\b.*$/i, '')
    .replace(/[\s,\-·]+$/g, '')
    .trim()
}

const SCOPE_KEYWORDS = [
  { match: /card\s*[&+]\s*box/i, value: 'Card & Box' },
  { match: /with\s+card/i, value: 'With Card' },
  { match: /with\s+box/i, value: 'With Box' },
  { match: /watch\s+only/i, value: 'Watch Only' },
]

const METAL_KEYWORDS = [
  { match: /yellow\s*gold/i, value: 'Yellow Gold' },
  { match: /pink\s*gold|rose\s*gold/i, value: 'Pink Gold' },
  { match: /white\s*gold/i, value: 'White Gold' },
  { match: /platinum/i, value: 'Platinum' },
]

const JEWELLERY_TYPE_KEYWORDS = [
  { match: /\bearrings?\b|\bstuds?\b|\bhoops?\b/i, value: 'Earrings' },
  { match: /\bbracelets?\b/i, value: 'Bracelets' },
  { match: /\bnecklaces?\b|\bpendant/i, value: 'Necklaces' },
  { match: /\brings?\b/i, value: 'Rings' },
]

function parseQuickPost(text) {
  const result = { ...EMPTY_FORM }
  if (!text.trim()) return result

  const lines = text.split('\n').map(l => l.trim()).filter(Boolean)
  const fullText = text // keep for keyword scanning

  // --- Brand detection (scan all lines) ---
  const sortedBrands = [...BRANDS].sort((a, b) => b.length - a.length)
  const fullLower = fullText.toLowerCase()
  let brandFoundInText = false
  for (const brand of sortedBrands) {
    if (fullLower.includes(brand.toLowerCase())) { result.brand = brand; brandFoundInText = true; break }
  }
  const abbreviations = { 'Audemars Piguet': /\bAP\b/, 'Patek Philippe': /\bPP\b/, 'Vacheron Constantin': /\bVC\b/i, 'Jaeger-LeCoultre': /\bJLC\b/, 'Richard Mille': /\bRM\b/, 'Van Cleef & Arpels': /\bVCA\b/, 'Louis Vuitton': /\bLV\b/ }
  for (const [brand, re] of Object.entries(abbreviations)) {
    if (re.test(fullText)) { result.brand = brand; brandFoundInText = true; break }
  }

  // Helper: parse a price string (58'000€, €35,000, 52.320, 13050)
  function parsePrice(str) {
    const m = str.match(/([\d]['\d,.\s]*[\d])/)
    if (!m) return ''
    const raw = m[1].replace(/['\s]/g, '')
    const dots = (raw.match(/\./g) || []).length
    const commas = (raw.match(/,/g) || []).length
    let num
    if (dots > 1) num = raw.replace(/\./g, '')
    else if (commas > 1) num = raw.replace(/,/g, '')
    else if (dots === 1 && commas > 0) num = raw.replace(/[.,]/g, '')
    else if (commas === 1 && raw.indexOf(',') > raw.length - 4) num = raw.replace(',', '.')
    else num = raw.replace(/,/g, '')
    return String(Math.round(Number(num)))
  }

  // --- Process each line ---
  let modelLine = ''
  const noteLines = []
  for (const line of lines) {
    const lineLower = line.toLowerCase()

    // Net/cost price line: "Net Price 52'320€" or "Net - 15'250€ - 17'850$ | WeChat: c713671"
    if (/\bnet\b/i.test(lineLower) && /\d/.test(line)) {
      // Extract vendor if after | on same line
      if (line.includes('|')) {
        const [pricePart, vendorPart] = line.split('|').map(s => s.trim())
        const eurNet = pricePart.match(/([\d]['\d,.\s]*[\d])\s*€/)
        result.cost_eur = eurNet ? parsePrice(eurNet[0]) : parsePrice(pricePart)
        const vendorMatch = vendorPart.match(/(?:vendor|wechat)[:\s]*(.+)/i)
        if (vendorMatch) result.vendor = vendorMatch[1].trim()
        else result.vendor = vendorPart
      } else {
        const eurNet = line.match(/([\d]['\d,.\s]*[\d])\s*€/)
        result.cost_eur = eurNet ? parsePrice(eurNet[0]) : parsePrice(line)
      }
      continue
    }

    // Price line: has € or $ or apostrophe-separated number, or just digits
    const hasPrice = /[€$]/.test(line) || /\d[']\d/.test(line) || /^[\d',.€$\s\-]+$/.test(line.trim())
    if (hasPrice && /\d{3,}/.test(line.replace(/'/g, ''))) {
      const eurMatch = line.match(/([\d]['\d,.\s]*[\d])\s*€/)
      const parsed = eurMatch ? parsePrice(eurMatch[0]) : parsePrice(line)
      if (!result.price_eur) result.price_eur = parsed
      else if (!result.cost_eur) result.cost_eur = parsed
      continue
    }

    // Condition line
    if (/pre-owned|minor|major|\bfair\b|needs?\s*repair|repaired/i.test(lineLower)) {
      if (/major/i.test(line)) result.condition = 'pre-owned conditions with MAJOR signs of usage'
      else if (/minor/i.test(line)) result.condition = 'pre-owned conditions with MINOR signs of usage'
      else if (/repair.*albania/i.test(line)) result.condition = 'Repaired Albania'
      else if (/repaired/i.test(line)) result.condition = 'Repaired'
      else if (/needs?\s*repair/i.test(line)) result.condition = 'Needs Repair'
      else if (/\bfair\b/i.test(line)) result.condition = 'Fair'
      continue
    }

    // Scope line (also handle "Card only" → "With Card", "Box only" → "With Box")
    let matchedScope = false
    if (/\bfull\s+set\b/i.test(line)) { result.scope_of_delivery = 'Card & Box'; matchedScope = true }
    else if (/\bcard\s+only\b/i.test(line)) { result.scope_of_delivery = 'With Card'; matchedScope = true }
    else if (/\bbox\s+only\b/i.test(line)) { result.scope_of_delivery = 'With Box'; matchedScope = true }
    else {
      for (const s of SCOPE_KEYWORDS) {
        if (s.match.test(line)) { result.scope_of_delivery = s.value; matchedScope = true; break }
      }
    }
    if (matchedScope) {
      const yearMatch = line.match(/\b((?:19|20)\d{2})\b/)
      if (yearMatch) noteLines.push(yearMatch[1])
      continue
    }

    // Vendor line → only treat as keyword if followed by colon (e.g. "Vendor: John", "WeChat: c713671")
    if (/\b(?:vendor|wechat):/i.test(lineLower)) {
      const vendorMatch = line.match(/(?:vendor|wechat)[:\s]*(.+)/i)
      if (vendorMatch) result.vendor = vendorMatch[1].trim()
      continue
    }

    // Metal type line
    let matchedMetal = false
    for (const m of METAL_KEYWORDS) {
      if (m.match.test(line)) { result.metal_type = m.value; matchedMetal = true; break }
    }
    if (matchedMetal) continue

    // First unmatched line → brand + model only if brand was in text; otherwise → vendor
    if (!modelLine && brandFoundInText) modelLine = line
    else if (!result.vendor) result.vendor = line
  }

  // Combine note lines into notes
  if (noteLines.length > 0) result.notes = noteLines.join(', ')

  // --- Extract model from the brand+model line ---
  if (modelLine) {
    let model = modelLine
    // Strip brand name
    const brandIdx = model.toLowerCase().indexOf(result.brand.toLowerCase())
    if (brandIdx !== -1) {
      model = model.slice(brandIdx + result.brand.length).trim()
    } else {
      // Try abbreviation
      for (const [brand, re] of Object.entries(abbreviations)) {
        if (brand === result.brand) {
          const m = model.match(re)
          if (m) { model = model.slice(m.index + m[0].length).trim(); break }
        }
      }
    }
    // Strip inline price if present
    model = model.replace(/[€$]\s*[\d,.]+/g, '').trim()
    // Strip condition/scope keywords if on the same line
    model = model.replace(/\b(pre-owned|minor|major|fair|needs?\s*repair|repaired|card\s*[&+]\s*box|with\s+card|with\s+box|watch\s+only)\b.*/gi, '').trim()
    model = model.replace(/^[\s,\-·]+|[\s,\-·]+$/g, '')

    // Use the code to look up model name (don't auto-fill reference/SKU field)
    const refMatch = model.match(/\b([A-Z0-9][A-Z0-9.\-/]{3,}[A-Z0-9])\b/i)
    if (refMatch) {
      // Look up model name from reference database
      const ref = refMatch[0]
      const refClean = ref.replace(/-/g, '')
      // Strip variant suffix: 126158-0012 → 126158, also try without dashes
      const refBase = ref.split('-')[0]
      const looked = WATCH_REFS[ref] || WATCH_REFS[refClean] || WATCH_REFS[refBase]
        // Try with common prefixes stripped or added (e.g. Q3838420 ↔ 3838420, PAM00111 ↔ 111)
        || Object.entries(WATCH_REFS).find(([k]) => k.endsWith(refClean) || k.endsWith(refBase) || refClean.endsWith(k.replace(/^[A-Z]+/, '')))?.[1]
        // Try partial match: ref starts with or is contained in a key
        || Object.entries(WATCH_REFS).find(([k]) => ref.startsWith(k) || refBase.startsWith(k) || k.includes(refBase))?.[1]
      if (looked) {
        // Model name + full reference: "Daytona 126518-0012"
        result.model = `${looked} ${ref}`
        return result
      }
    }

    // No lookup found — use the raw text as model (includes the ref code)
    if (model) result.model = model
  }

  // --- Category detection ---
  const brandLower = result.brand.toLowerCase()
  if (/\bbag\b|\bbirkin\b|\bkelly\b|\bneverfull\b|\bspeedy\b|\btote\b|\bclutch\b/i.test(fullText)) {
    result.category = 'Bags'
  } else if (/\bjewel|\bring\b|\bbracelet\b|\bnecklace\b|\bearring|\bpendant/i.test(fullText) || JEWELLERY_BRANDS.has(brandLower)) {
    result.category = 'Jewellery'
  } else {
    result.category = 'Watches'
  }
  if (['rolex','audemars piguet','patek philippe','omega','iwc','jaeger-lecoultre','breitling','tag heuer','tudor','hublot','richard mille','vacheron constantin','a. lange & söhne','panerai','blancpain','breguet','zenith','grand seiko','ulysse nardin','girard-perregaux'].includes(brandLower)) {
    result.category = 'Watches'
  }

  // Jewellery sub-type
  for (const j of JEWELLERY_TYPE_KEYWORDS) {
    if (j.match.test(fullText)) { result.subcategory = j.value; break }
  }

  return result
}

const EMPTY_FORM = {
  category: 'Watches',
  brand: 'Rolex',
  model: '',
  reference: '',
  condition: 'Pre-owned',
  scope_of_delivery: '',
  price_eur: '',
  cost_eur: '',
  vendor: '',
  notes: '',
  metal_type: '',
  item_size: '',
  subcategory: '',
  is_preorder: false
}

const SUPABASE_URL = 'https://tulqgebsvpxgwocptnmy.supabase.co'
const ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InR1bHFnZWJzdnB4Z3dvY3B0bm15Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQ2MjYzOTEsImV4cCI6MjA5MDIwMjM5MX0.H12dPM59cIxlvpR7jbuDjpX11qNdohvi-nhiMxNheJA'

async function notifyOffer(payload) {
  try {
    await fetch(`${SUPABASE_URL}/functions/v1/notify-offer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${ANON_KEY}` },
      body: JSON.stringify(payload)
    })
  } catch (err) {
    console.log('Notify error:', err)
  }
}

async function notifyDealers(watch) {
  try {
    await fetch('https://tulqgebsvpxgwocptnmy.supabase.co/functions/v1/notify-dealers', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InR1bHFnZWJzdnB4Z3dvY3B0bm15Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQ2MjYzOTEsImV4cCI6MjA5MDIwMjM5MX0.H12dPM59cIxlvpR7jbuDjpX11qNdohvi-nhiMxNheJA'
      },
      body: JSON.stringify({ record: watch })
    })
  } catch (err) {
    console.log('Notify error:', err)
  }
}

async function notifyN8nBagPreorder(item, imageUrls = []) {
  try {
    const res = await fetch('/api/n8n-bag-preorder', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event: 'bag_preorder_created',
        item,
        image_urls: imageUrls,
      }),
    })
    const result = await res.json().catch(() => null)
    if (!result?.ok) console.warn('n8n bag preorder webhook did not complete:', result)
  } catch (err) {
    console.error('n8n bag preorder webhook failed:', err)
  }
}

const PAGE_SIZE = 100

export default function AgentListings() {
  const { profile } = useAuth()
  const navigate = useNav()
  const location = useLocation()
  const { rate, refetch: refetchEurUsd } = useExchangeRate()
  const { rate: usdToCnyRate, refetch: refetchUsdCny } = useExchangeRate('USD', 'CNY')
  const { rate: hkdToEurRate, refetch: refetchHkdEur } = useExchangeRate('HKD', 'EUR')
  const [tab, setTab] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('tab') || 'listings'
  })

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const t = params.get('tab')
    if (t) setTab(t)
  }, [location.search])
  const [bagName, setBagName] = useState('')
  const [bagCategory, setBagCategory] = useState('Bags')
  const [bagBrand, setBagBrand] = useState('Other')
  const [bagModel, setBagModel] = useState('')
  const [bagCondition, setBagCondition] = useState('Preowned')
  const [bagCostPrice, setBagCostPrice] = useState('')
  const [bagCostCurrency, setBagCostCurrency] = useState('EUR')
  const [bagSellingPrice, setBagSellingPrice] = useState('')
  const [bagSellingCurrency, setBagSellingCurrency] = useState('EUR')
  const [bagPosting, setBagPosting] = useState(false)
  const [bagMsg, setBagMsg] = useState('')
  const [bagError, setBagError] = useState('')
  const [bagImages, setBagImages] = useState([])
  const [bagPreviews, setBagPreviews] = useState([])
  const [bagDragIndex, setBagDragIndex] = useState(null)
  const bagImgUploadRef = useRef(null)
  const [bagIsPreorder, setBagIsPreorder] = useState(false)
  const [preorderReposts, setPreorderReposts] = useState({})
  const [repostingId, setRepostingId] = useState(null)
  const [watches, setWatches] = useState([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState(EMPTY_FORM)
  const [images, setImages] = useState([])
  const [previews, setPreviews] = useState([])
  const [posting, setPosting] = useState(false)
  const [msg, setMsg] = useState('')
  const [error, setError] = useState('')
  const { currency } = useCurrency()
  const [search, setSearch] = useState('')
  const [offers, setOffers] = useState([])
  const [offersLoading, setOffersLoading] = useState(false)
  const [counterInputs, setCounterInputs] = useState({})
  const [agentComments, setAgentComments] = useState({})
  const [counterOpen, setCounterOpen] = useState({})
  const [offerStatusTab, setOfferStatusTab] = useState('pending')
  const [preorders, setPreorders] = useState([])
  const [preordersPage, setPreordersPage] = useState(0)
  const [preordersTotal, setPreordersTotal] = useState(null)
  const [expandedImgPreorder, setExpandedImgPreorder] = useState(null)
  const [watchPreordersTotal, setWatchPreordersTotal] = useState(null)
  const [bagPreordersTotal, setBagPreordersTotal] = useState(null)
  const [jewelleryPreordersTotal, setJewelleryPreordersTotal] = useState(null)
  const [archivedPreordersTotal, setArchivedPreordersTotal] = useState(null)
  const [watchesPage, setWatchesPage] = useState(0)
  const [watchesTotal, setWatchesTotal] = useState(null)
  const [listingType, setListingType] = useState('instock')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [preorderStatusFilter, setPreorderStatusFilter] = useState('all')
  const [preorderBrandFilter, setPreorderBrandFilter] = useState('all')
  const [preorderPriceMin, setPreorderPriceMin] = useState('')
  const [preorderPriceMax, setPreorderPriceMax] = useState('')
  const [preorderSort, setPreorderSort] = useState('newest')
  const [clients, setClients] = useState([])
  const [pendingTokens, setPendingTokens] = useState([])
  const [clientsLoading, setClientsLoading] = useState(false)
  const [generatedLink, setGeneratedLink] = useState('')
  const [generating, setGenerating] = useState(false)
  const [linkError, setLinkError] = useState('')
  const [copied, setCopied] = useState('')
  const [supplierListings, setSupplierListings] = useState([])
  const [supReviewData, setSupReviewData] = useState({}) // { [id]: { price, reason } }
  const [supEditId, setSupEditId] = useState(null)
  const [supEditForm, setSupEditForm] = useState({})
  const [supEditExistingImgs, setSupEditExistingImgs] = useState([])
  const [supEditRemovedIds, setSupEditRemovedIds] = useState([])
  const [supEditNewFiles, setSupEditNewFiles] = useState([])
  const [supEditNewPreviews, setSupEditNewPreviews] = useState([])
  const [lbImgs, setLbImgs] = useState([])
  const [lbIdx, setLbIdx] = useState(null)
  const [currentRatesData, setCurrentRatesData] = useState({})
  const [rateInputs, setRateInputs] = useState({})
  const [savingRate, setSavingRate] = useState(null)
  const [rateMsg, setRateMsg] = useState('')
  const [translatingNotes, setTranslatingNotes] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteName, setInviteName] = useState('')
  const [inviteRole, setInviteRole] = useState('dealer')
  const [inviting, setInviting] = useState(false)
  const [inviteMsg, setInviteMsg] = useState('')
  const [inviteError, setInviteError] = useState('')
  const openLb = (urls, idx) => { setLbImgs(urls); setLbIdx(idx) }
  const closeLb = () => { setLbIdx(null); setLbImgs([]) }
  const lbPrev = e => { if (e) e.stopPropagation(); setLbIdx(i => (i - 1 + lbImgs.length) % lbImgs.length) }
  const lbNext = e => { if (e) e.stopPropagation(); setLbIdx(i => (i + 1) % lbImgs.length) }

  useEffect(() => {
    if (lbIdx === null) return
    const handler = e => {
      if (e.key === 'ArrowRight') lbNext()
      else if (e.key === 'ArrowLeft') lbPrev()
      else if (e.key === 'Escape') closeLb()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [lbIdx, lbImgs])
  const [listingsOpen, setListingsOpen] = useState(true)
  const [activityOpen, setActivityOpen] = useState(true)
  const [inStockBrand, setInStockBrand] = useState('all')
  const [inStockCondition, setInStockCondition] = useState('all')
  const [inStockStatus, setInStockStatus] = useState('all')
  const [inStockSortBy, setInStockSortBy] = useState('newest')
  const [viewMode, setViewMode] = useState('list')

  const fetchSupplierListings = useCallback(async () => {
    let query = supabase
      .from('supplier_listings')
      .select('*, supplier_listing_images(id, url, position), profiles!supplier_id(full_name, phone)')
      .eq('status', 'pending_review')
      .order('created_at', { ascending: false })

    if (profile?.role === 'jewellery_agent') {
      query = query.eq('category', 'Jewellery')
      const { data: assigned } = await supabase.from('profiles').select('id').eq('assigned_agent_id', profile.id)
      const ids = (assigned || []).map(s => s.id)
      if (ids.length === 0) { setSupplierListings([]); return }
      query = query.in('supplier_id', ids)
    } else if (profile?.role === 'agent') {
      query = query.or('category.is.null,category.eq.Watches')
      const { data: assigned } = await supabase.from('profiles').select('id').eq('assigned_agent_id', profile.id)
      const ids = (assigned || []).map(s => s.id)
      if (ids.length === 0) { setSupplierListings([]); return }
      query = query.in('supplier_id', ids)
    }
    // admin sees all pending
    const { data } = await query
    setSupplierListings(data || [])
  }, [profile])

  const fetchMyWatches = useCallback(async (page = 0, searchTerm = '') => {
    let base = profile?.role === 'admin'
      ? supabase.from('products').select('*, product_images(url, position)', { count: 'exact' }).order('created_at', { ascending: false })
      : supabase.from('products').select('*, product_images(url, position)', { count: 'exact' }).eq('posted_by', profile?.id).order('created_at', { ascending: false })
    if (searchTerm.trim()) {
      const s = searchTerm.trim()
      base = base.or(`brand.ilike.%${s}%,model.ilike.%${s}%,reference.ilike.%${s}%`)
    }
    const { data, count } = await base.range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1)
    if (page === 0) setWatches(data || [])
    else setWatches(prev => [...prev, ...(data || [])])
    if (count !== null) setWatchesTotal(count)
    setWatchesPage(page)
    setLoading(false)
  }, [profile])

  const fetchPreorders = useCallback(async (page = 0, type = null, searchTerm = '') => {
    const now = new Date().toISOString()
    let query = supabase
      .from('preorders')
      .select('*, preorder_images(id, url, position)', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1)
    if (type === 'preorders-watches') {
      query = query.or('category.is.null,category.eq.Watches').or('expires_at.is.null,expires_at.gte.' + now)
    } else if (type === 'preorders-jewellery') {
      query = query.eq('category', 'Jewellery')
    } else if (type === 'preorders-bags') {
      query = query.eq('category', 'Bags')
    } else if (type === 'preorders-archived') {
      query = query.or('category.is.null,category.eq.Watches').not('expires_at', 'is', null).lt('expires_at', now)
    }
    if (searchTerm.trim()) {
      const s = searchTerm.trim()
      query = query.or(`brand.ilike.%${s}%,model.ilike.%${s}%,reference.ilike.%${s}%`)
    }
    const { data, count } = await query
    if (page === 0) setPreorders(data || [])
    else setPreorders(prev => [...prev, ...(data || [])])
    if (count !== null) setPreordersTotal(count)
    setPreordersPage(page)
  }, [])

  const fetchOffers = useCallback(async () => {
    setOffersLoading(true)
    const { data, error } = await supabase
      .from('offers')
      .select('*, products(id, brand, model, reference, price_eur, price_usd, product_images(url, position))')
      .order('created_at', { ascending: false })
    if (error) console.error('fetchOffers error:', error)
    setOffers(data || [])
    setOffersLoading(false)
  }, [])

  const fetchClients = useCallback(async () => {
    if (!profile?.id) return
    setClientsLoading(true)
    const [{ data: dealers }, { data: tokens }] = await Promise.all([
      supabase.from('profiles').select('id, full_name, email, phone, created_at').eq('invited_by', profile.id).order('created_at', { ascending: false }),
      supabase.from('invite_tokens').select('id, token, created_at').eq('created_by', profile.id).eq('used', false).order('created_at', { ascending: false })
    ])
    setClients(dealers || [])
    setPendingTokens(tokens || [])
    setClientsLoading(false)
  }, [profile])

  async function handleGenerateLink() {
    setLinkError(''); setGenerating(true)
    const tokenBytes = new Uint8Array(20)
    crypto.getRandomValues(tokenBytes)
    const token = Array.from(tokenBytes).map(b => b.toString(16).padStart(2, '0')).join('')
    const { error } = await supabase.from('invite_tokens').insert({ token, created_by: profile.id })
    if (error) { setLinkError(error.message); setGenerating(false); return }
    setGeneratedLink(`${window.location.origin}/join/${token}`)
    setGenerating(false)
    fetchClients()
  }

  async function handleRevokeToken(id) {
    await supabase.from('invite_tokens').delete().eq('id', id)
    fetchClients()
    setGeneratedLink(prev => prev) // keep shown link as-is
  }

  function handleCopy(text, key) {
    navigator.clipboard.writeText(text)
    setCopied(key)
    setTimeout(() => setCopied(''), 2000)
  }

  useEffect(() => { if (profile) fetchMyWatches() }, [profile, fetchMyWatches])

  // Fetch type-specific preorders when the sub-tab changes (passes current search term)
  useEffect(() => {
    if (profile && listingType.startsWith('preorders-')) {
      setPreorders([])
      setPreordersPage(0)
      setPreordersTotal(null)
      fetchPreorders(0, listingType, search)
    }
  }, [profile, listingType, fetchPreorders]) // search excluded — its changes handled by debounce effect below

  const fetchPreorderReposts = useCallback(async (bagIds) => {
    if (bagIds.length === 0) { setPreorderReposts({}); return }
    const { data, error } = await supabase
      .from('repost_requests')
      .select('id, preorder_id, status, requested_at, completed_at, error_message')
      .in('preorder_id', bagIds)
      .order('requested_at', { ascending: false })
    if (error) { console.error('fetch preorder reposts error:', error); setPreorderReposts({}); return }
    const latestByPreorder = {}
    for (const row of data || []) {
      if (!latestByPreorder[row.preorder_id]) latestByPreorder[row.preorder_id] = row
    }
    setPreorderReposts(latestByPreorder)
  }, [])

  useEffect(() => {
    if (listingType !== 'preorders-bags') return
    fetchPreorderReposts(preorders.map(p => p.id))
  }, [preorders, listingType, fetchPreorderReposts])

  // Live-update repost status (e.g. n8n marking a request completed/failed)
  // without needing a manual refresh — same pattern as Topbar/DealerCatalog.
  useEffect(() => {
    if (listingType !== 'preorders-bags') return
    const sub = supabase.channel('repost-requests-bags')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'repost_requests' }, () => {
        fetchPreorderReposts(preorders.map(p => p.id))
      })
      .subscribe()
    return () => supabase.removeChannel(sub)
  }, [listingType, preorders, fetchPreorderReposts])

  // Debounced server-side search — fires 350ms after the user stops typing
  useEffect(() => {
    if (!profile) return
    const timer = setTimeout(() => {
      if (listingType.startsWith('preorders-')) {
        setPreorders([])
        setPreordersPage(0)
        setPreordersTotal(null)
        fetchPreorders(0, listingType, search)
      } else if (listingType === 'instock') {
        setWatches([])
        setWatchesPage(0)
        setWatchesTotal(null)
        setLoading(true)
        fetchMyWatches(0, search)
      }
    }, 350)
    return () => clearTimeout(timer)
  }, [search, profile]) // listingType excluded — tab switches handled by effect above

  // Lightweight count-only queries so sidebar + pill badges always show real totals
  useEffect(() => {
    if (!profile) return
    const now = new Date().toISOString()
    supabase.from('preorders').select('*', { count: 'exact', head: true })
      .or('category.is.null,category.eq.Watches').or('expires_at.is.null,expires_at.gte.' + now)
      .then(({ count }) => setWatchPreordersTotal(count ?? 0))
    supabase.from('preorders').select('*', { count: 'exact', head: true })
      .eq('category', 'Jewellery')
      .then(({ count }) => setJewelleryPreordersTotal(count ?? 0))
    supabase.from('preorders').select('*', { count: 'exact', head: true })
      .eq('category', 'Bags')
      .then(({ count }) => setBagPreordersTotal(count ?? 0))
    supabase.from('preorders').select('*', { count: 'exact', head: true })
      .or('category.is.null,category.eq.Watches').not('expires_at', 'is', null).lt('expires_at', now)
      .then(({ count }) => setArchivedPreordersTotal(count ?? 0))
  }, [profile])

  useEffect(() => { if (profile && tab === 'offers') fetchOffers() }, [profile, tab, fetchOffers])
  useEffect(() => { if (profile && tab === 'clients') fetchClients() }, [profile, tab, fetchClients])
  useEffect(() => { if (profile && tab === 'supplier') fetchSupplierListings() }, [profile, tab, fetchSupplierListings])
  useEffect(() => { if (tab === 'rates') fetchCurrentRatesData() }, [tab])

  const RATE_PAIRS = [
    { from: 'USD', to: 'CNY', label: 'USD → CNY', desc: 'US Dollar to Chinese Yuan — local exchange rate (used to convert supplier CNY prices via USD → EUR)' },
    { from: 'HKD', to: 'EUR', label: 'HKD → EUR', desc: 'Hong Kong Dollar to Euro — supplier price conversion' },
    { from: 'EUR', to: 'USD', label: 'EUR → USD', desc: 'Euro to US Dollar — second leg of CNY conversion + display' },
  ]

  async function handleInvite(e) {
    e.preventDefault()
    setInviting(true); setInviteMsg(''); setInviteError('')
    try {
      const res = await fetch('https://tulqgebsvpxgwocptnmy.supabase.co/functions/v1/send-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}` },
        body: JSON.stringify({ email: inviteEmail, role: inviteRole, full_name: inviteName || inviteEmail.split('@')[0] }),
      })
      const result = await res.json()
      if (!res.ok) throw new Error(result.error || 'Invite failed')
      setInviteMsg(`Invite sent to ${inviteEmail}`)
      setInviteEmail(''); setInviteName('')
    } catch (err) {
      setInviteError(err.message)
    } finally {
      setInviting(false)
    }
  }

  async function fetchCurrentRatesData() {
    const results = {}
    for (const { from, to } of RATE_PAIRS) {
      const { data } = await supabase
        .from('exchange_rates')
        .select('rate, fetched_at')
        .eq('from_currency', from)
        .eq('to_currency', to)
        .order('fetched_at', { ascending: false })
        .limit(1)
      if (data?.[0]) results[`${from}-${to}`] = data[0]
    }
    setCurrentRatesData(results)
    const inputs = {}
    for (const { from, to } of RATE_PAIRS) {
      const key = `${from}-${to}`
      if (results[key]) inputs[key] = String(results[key].rate)
    }
    setRateInputs(prev => ({ ...inputs, ...prev }))
  }

  async function handleSaveRate(from, to, value) {
    const num = parseFloat(value)
    if (!num || num <= 0) return
    const key = `${from}-${to}`
    setSavingRate(key)
    try {
      const { error } = await supabase.from('exchange_rates').insert({
        from_currency: from, to_currency: to, rate: num,
        fetched_at: new Date().toISOString(),
      })
      if (error) throw error
      setRateMsg(`${from} → ${to} set to ${num}`)
      setTimeout(() => setRateMsg(''), 4000)
      fetchCurrentRatesData()
      if (from === 'USD' && to === 'CNY') refetchUsdCny()
      if (from === 'HKD' && to === 'EUR') refetchHkdEur()
      if (from === 'EUR' && to === 'USD') refetchEurUsd()
    } catch (e) { alert('Failed to save: ' + e.message) }
    setSavingRate(null)
  }

  const CURRENCY_SYMBOLS = { EUR: '€', USD: '$', CNY: '¥', HKD: 'HK$' }

  async function approveSupplierListing(listing) {
    if (listing.preorder_id) { alert('This listing has already been approved and a preorder created.'); return }
    const rd = supReviewData[listing.id] || {}
    if (!rd.price) { alert('Set a selling price before approving.'); return }
    const cur = rd.currency || 'EUR'
    const sym = CURRENCY_SYMBOLS[cur] || cur + ' '
    if (!window.confirm(`Approve and publish as preorder at ${sym}${rd.price}?`)) return

    let priceEur, priceUsd
    const num = Number(rd.price)
    if (cur === 'USD') {
      priceUsd = num
      priceEur = rate ? Math.round(num / rate) : num
    } else if (cur === 'CNY') {
      priceEur = (usdToCnyRate && rate) ? Math.round(num / usdToCnyRate / rate) : num
      priceUsd = priceEur && rate ? Math.round(priceEur * rate) : null
    } else if (cur === 'HKD') {
      priceEur = hkdToEurRate ? Math.round(num * hkdToEurRate) : num
      priceUsd = priceEur && rate ? Math.round(priceEur * rate) : null
    } else {
      priceEur = num
      priceUsd = rate ? Math.round(num * rate) : null
    }

    const askingCur = listing.asking_price_currency || 'CNY'
    const askingAmt = listing.asking_price ? Number(listing.asking_price) : null
    let costEur = null
    if (askingAmt) {
      if (askingCur === 'EUR') costEur = askingAmt
      else if (askingCur === 'CNY' && usdToCnyRate && rate) costEur = Math.round(askingAmt / usdToCnyRate / rate)
      else if (askingCur === 'HKD' && hkdToEurRate) costEur = Math.round(askingAmt * hkdToEurRate)
      else if (askingCur === 'USD' && rate) costEur = Math.round(askingAmt / rate)
    }

    const isJewellery = listing.category === 'Jewellery'
    // For jewellery: encode stone + size into notes if present
    let notesForPreorder = listing.notes || null
    if (isJewellery) {
      const extras = [listing.stone, listing.size_info].filter(Boolean).join(' · ')
      notesForPreorder = [listing.notes, extras].filter(Boolean).join('\n') || null
    }
    const { data: preorder, error: pErr } = await supabase.from('preorders').insert({
      brand: isJewellery ? (listing.brand || listing.jewellery_type || 'Jewellery') : listing.brand,
      model: isJewellery ? (listing.model || listing.jewellery_type || '') : listing.model,
      reference: isJewellery ? null : (listing.reference || null),
      condition: listing.condition || 'Pre-owned',
      scope_of_delivery: isJewellery ? null : (listing.scope_of_delivery || null),
      notes: notesForPreorder,
      price_eur: priceEur,
      price_usd: priceUsd,
      cost_eur: costEur,
      cost_cny: (askingCur === 'CNY' && askingAmt) ? askingAmt : null,
      vendor: listing.profiles?.full_name || null,
      category: isJewellery ? 'Jewellery' : 'Watches',
      subcategory: isJewellery ? (listing.jewellery_type || null) : null,
      metal_type: isJewellery ? (listing.metal || null) : null,
      // Jewellery listings don't expire; watches use 7-day rolling window
      expires_at: isJewellery ? null : new Date(Date.now() + 7 * 86400000).toISOString(),
      posted_by: profile.id,
      status: 'available',
    }).select().single()
    if (pErr) { alert('Failed to create preorder: ' + pErr.message); return }
    const imgs = (listing.supplier_listing_images || []).sort((a, b) => a.position - b.position)
    for (let imgIdx = 0; imgIdx < imgs.length; imgIdx++) {
      await supabase.from('preorder_images').insert({ preorder_id: preorder.id, url: imgs[imgIdx].url, position: imgIdx })
    }
    const { error: slErr } = await supabase.from('supplier_listings').update({
      status: 'approved',
      selling_price: Number(rd.price),
      reviewed_by: profile.id,
      reviewed_at: new Date().toISOString(),
      preorder_id: preorder.id,
    }).eq('id', listing.id)
    if (slErr) {
      // Preorder was created — force the status update directly so the listing
      // doesn't reappear in the queue and get double-approved.
      console.error('supplier_listing status update failed:', slErr.message)
      await supabase.from('supplier_listings').update({ status: 'approved', preorder_id: preorder.id }).eq('id', listing.id)
    }
    fetchSupplierListings()
    fetchPreorders(0, listingType, search)
  }

  async function rejectSupplierListing(listing) {
    const rd = supReviewData[listing.id] || {}
    if (!window.confirm('Reject this listing?')) return
    await supabase.from('supplier_listings').update({
      status: 'rejected',
      reviewed_by: profile.id,
      reviewed_at: new Date().toISOString(),
      rejection_reason: rd.reason,
    }).eq('id', listing.id)
    fetchSupplierListings()
  }

  async function translateText(text) {
    if (!text || !/[一-鿿㐀-䶿＀-￯]/.test(text)) return text
    try {
      const res = await fetch(
        `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=zh|en`
      )
      const data = await res.json()
      if (data.responseStatus === 200 && data.responseData?.translatedText) {
        return data.responseData.translatedText
      }
    } catch {}
    return text
  }

  function enterSupEdit(listing) {
    setSupEditId(listing.id)
    const notes = listing.notes || ''
    setSupEditForm({
      brand: listing.brand || '',
      model: listing.model || '',
      reference: listing.reference || '',
      condition: listing.condition || '',
      scope_of_delivery: listing.scope_of_delivery || '',
      asking_price: listing.asking_price || '',
      notes,
      // Jewellery-specific fields
      jewellery_type: listing.jewellery_type || '',
      metal: listing.metal || '',
      stone: listing.stone || '',
      size_info: listing.size_info || '',
    })
    setSupEditExistingImgs((listing.supplier_listing_images || []).sort((a, b) => a.position - b.position))
    setSupEditRemovedIds([])
    setSupEditNewFiles([])
    setSupEditNewPreviews([])

    // Auto-translate notes if they contain Chinese characters
    if (notes && /[一-鿿㐀-䶿]/.test(notes)) {
      setTranslatingNotes(true)
      translateText(notes).then(translated => {
        setSupEditForm(f => ({ ...f, notes: translated }))
        setTranslatingNotes(false)
      })
    }
  }

  async function saveSupplierListingEdit(listingId) {
    const listing = supplierListings.find(l => l.id === listingId)
    const isJewellery = listing?.category === 'Jewellery'
    const updatedFields = {
      brand: supEditForm.brand,
      model: supEditForm.model,
      condition: supEditForm.condition || null,
      asking_price: supEditForm.asking_price ? Number(supEditForm.asking_price) : null,
      notes: supEditForm.notes || null,
      // Watch-specific
      reference: isJewellery ? (listing?.reference || null) : (supEditForm.reference || null),
      scope_of_delivery: isJewellery ? null : (supEditForm.scope_of_delivery || null),
      // Jewellery-specific
      jewellery_type: isJewellery ? (supEditForm.jewellery_type || null) : null,
      metal: isJewellery ? (supEditForm.metal || null) : null,
      stone: isJewellery ? (supEditForm.stone || null) : null,
      size_info: isJewellery ? (supEditForm.size_info || null) : null,
    }

    const { error } = await supabase
      .from('supplier_listings')
      .update(updatedFields)
      .eq('id', listingId)
    if (error) { alert('Failed to save: ' + error.message); return }

    for (const id of supEditRemovedIds) {
      await supabase.from('supplier_listing_images').delete().eq('id', id)
    }

    const startPos = supEditExistingImgs.filter(img => !supEditRemovedIds.includes(img.id)).length
    let newImgPos = 0
    for (let i = 0; i < supEditNewFiles.length; i++) {
      const file = supEditNewFiles[i]
      const ext = file.name.split('.').pop()
      const path = `supplier_listings/${listingId}/${Date.now()}_${i}.${ext}`
      const { error: upErr } = await supabase.storage.from('watch-images').upload(path, file)
      if (upErr) continue
      const { data: { publicUrl } } = supabase.storage.from('watch-images').getPublicUrl(path)
      await supabase.from('supplier_listing_images').insert({ listing_id: listingId, url: publicUrl, position: startPos + newImgPos })
      newImgPos++
    }

    // Optimistic update: immediately reflect edits in local state so the approve
    // button uses the fresh notes/fields without waiting for the DB refetch.
    setSupplierListings(prev => prev.map(l =>
      l.id === listingId ? { ...l, ...updatedFields } : l
    ))

    setSupEditId(null)
    fetchSupplierListings()
  }

  function handleField(k, v) { setForm(f => ({ ...f, [k]: v })) }

  function handleImages(e) {
    const files = Array.from(e.target.files)
    setImages(files)
    setPreviews(files.map(f => URL.createObjectURL(f)))
  }

  function handleBagImages(e) {
    const files = Array.from(e.target.files)
    setBagError('')
    setBagImages(prev => [...prev, ...files])
    setBagPreviews(prev => [...prev, ...files.map(f => URL.createObjectURL(f))])
    e.target.value = ''
  }

  function reorderBagImages(from, to) {
    if (from === null || from === to) return
    setBagImages(arr => { const copy = [...arr]; const [moved] = copy.splice(from, 1); copy.splice(to, 0, moved); return copy })
    setBagPreviews(arr => { const copy = [...arr]; const [moved] = copy.splice(from, 1); copy.splice(to, 0, moved); return copy })
  }

  function removeBagImage(idx) {
    setBagImages(arr => arr.filter((_, i) => i !== idx))
    setBagPreviews(arr => arr.filter((_, i) => i !== idx))
  }

  function handleBagName(value) {
    setBagError('')
    setBagName(value)
    const lines = value.split('\n').map(line => line.trim()).filter(Boolean)
    const brandMatch = detectBrand(value)
    const firstLine = lines[0] || ''
    let model = firstLine
    if (brandMatch) {
      const lineMatch = detectBrand(firstLine)
      if (lineMatch) model = firstLine.slice(lineMatch.index + lineMatch.length).trim()
    }
    model = stripBagCondition(model)

    const cost = parseBagPriceInput(lines[1])
    const selling = parseBagPriceInput(lines[2])

    setBagBrand(brandMatch?.brand || 'Other')
    setBagModel(model)
    setBagCondition(detectBagCondition(firstLine) || 'Preowned')
    setBagCostPrice(cost.amount)
    setBagSellingPrice(selling.amount)
    if (cost.currency) setBagCostCurrency(cost.currency === 'USD' ? 'EUR' : cost.currency)
    if (selling.currency) setBagSellingCurrency(selling.currency)
  }

  function isValidRate(value) {
    return Number.isFinite(Number(value)) && Number(value) > 0
  }

  function convertBagSellingPrices(amount, currencyCode, includeCny = true) {
    const sellingAmount = Number(amount)
    const eurUsdRate = Number(rate)
    const usdCnyRate = Number(usdToCnyRate)

    if (!Number.isFinite(sellingAmount) || sellingAmount <= 0) return null
    if (!isValidRate(eurUsdRate)) return null
    if (includeCny && !isValidRate(usdCnyRate)) return null

    if (currencyCode === 'USD') {
      const priceEur = sellingAmount / eurUsdRate
      return {
        priceEur,
        priceUsd: sellingAmount,
        priceCny: includeCny ? sellingAmount * usdCnyRate : null,
      }
    }

    if (currencyCode === 'CNY') {
      const priceUsd = sellingAmount / usdCnyRate
      return {
        priceEur: priceUsd / eurUsdRate,
        priceUsd,
        priceCny: sellingAmount,
      }
    }

    const priceUsd = sellingAmount * eurUsdRate
    return {
      priceEur: sellingAmount,
      priceUsd,
      priceCny: includeCny ? priceUsd * usdCnyRate : null,
    }
  }

  const usdPreview = form.price_eur && rate
    ? '$' + Math.round(Number(form.price_eur) * rate).toLocaleString()
    : null

  async function handlePost(e) {
    e.preventDefault()
    setError('')
    if (!form.brand) { setError('Brand is required.'); return }
    if (!form.model) { setError('Model name is required.'); return }
    // reference is optional for manual entries
    if (!form.condition) { setError('Condition is required.'); return }
    if (!form.price_eur) { setError('Price in EUR is required.'); return }
    if (form.category === 'Watches' && !form.scope_of_delivery) { setError('Scope of delivery is required.'); return }
    if (form.category === 'Jewellery' && !form.subcategory) { setError('Jewellery type is required.'); return }
    if (form.category === 'Jewellery' && !form.metal_type) { setError('Metal type is required.'); return }
    if (images.length === 0) { setError('At least one photo is required.'); return }
    setPosting(true)
    try {
      const priceUsd = rate ? Math.round(Number(form.price_eur) * rate) : null
      const payload = {
        category: form.category,
        brand: form.brand,
        model: form.model,
        reference: form.reference || null,
        condition: form.condition,
        price_eur: Number(form.price_eur),
        price_usd: priceUsd,
        cost_eur: form.cost_eur ? Number(form.cost_eur) : null,
        vendor: form.vendor || null,
        notes: form.notes || null,
        scope_of_delivery: form.scope_of_delivery || null,
        metal_type: form.category === 'Jewellery' && form.metal_type ? form.metal_type : null,
        subcategory: form.category === 'Jewellery' && form.subcategory ? form.subcategory : null,
        item_size: form.category === 'Jewellery' && form.item_size && form.subcategory !== 'Necklaces' ? form.item_size : null,
        posted_by: profile.id,
        status: 'available',
      }

      if (form.is_preorder) {
        const { data: preorder, error: pErr } = await supabase.from('preorders').insert(payload).select().single()
        if (pErr) throw pErr

        let imagesFailed = 0
        let imgPos = 0
        for (let i = 0; i < images.length; i++) {
          const file = images[i]
          const ext = file.name.split('.').pop()
          const path = `${preorder.id}/${i}.${ext}`
          const { error: upErr } = await supabase.storage.from('watch-images').upload(path, file)
          if (upErr) { console.error('Preorder image upload error:', upErr.message); imagesFailed++; continue }
          const { data: { publicUrl } } = supabase.storage.from('watch-images').getPublicUrl(path)
          const { error: dbErr } = await supabase.from('preorder_images').insert({ preorder_id: preorder.id, url: publicUrl, position: imgPos })
          if (dbErr) { console.error('Preorder image DB error:', dbErr.message); imagesFailed++ }
          else imgPos++
        }

        setForm(EMPTY_FORM)
        setImages([])
        setPreviews([])
        if (imagesFailed > 0) {
          setMsg(`Preorder posted, but ${imagesFailed} image(s) failed to save — open the listing and re-upload them.`)
        } else {
          setMsg('Preorder posted — dealers will be notified.')
        }
        setTab('listings')
        setListingType('preorders-watches')
        fetchPreorders(0, listingType, search)
      } else {
        const { data: watch, error: wErr } = await supabase.from('products').insert({ ...payload, source: 'manual' }).select().single()
        if (wErr) throw wErr

        for (let i = 0; i < images.length; i++) {
          const file = images[i]
          const ext = file.name.split('.').pop()
          const path = `${watch.id}/${i}.${ext}`
          const { error: upErr } = await supabase.storage.from('watch-images').upload(path, file)
          if (upErr) { console.error('Storage upload error:', upErr.message); throw new Error(`Image upload failed: ${upErr.message}`) }
          const { data: { publicUrl } } = supabase.storage.from('watch-images').getPublicUrl(path)
          const { error: imgErr } = await supabase.from('product_images').insert({ product_id: watch.id, url: publicUrl, position: i })
          if (imgErr) { console.error('product_images insert error:', imgErr.message); throw new Error(`Image save failed: ${imgErr.message}`) }
        }

        setForm(EMPTY_FORM)
        setImages([])
        setPreviews([])
        setMsg('Item posted — now live in the dealer catalog.')
        setTab('listings')
        fetchMyWatches()
      }
    } catch (err) {
      console.error('Post error:', err)
      setError(err?.message || 'Something went wrong. Please try again.')
    }
    setPosting(false)
  }

  async function handleBagPost(e) {
    e.preventDefault()
    setBagError('')
    if (!bagName.trim()) { setBagError('Name is required.'); return }
    if (!bagCostPrice && !bagSellingPrice) { setBagError('Cost price or selling price is required.'); return }
    if (!bagModel.trim()) { setBagError('Model name is required.'); return }
    if (bagImages.length === 0) {
      setBagError('At least one photo is required.')
      bagImgUploadRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    setBagPosting(true)
    try {
      const parsed = parseQuickPost(bagName)

      const costEur = bagCostPrice
        ? bagCostCurrency === 'CNY'
          ? Number(bagCostPrice) / (usdToCnyRate || 1) / (rate || 1)
          : Number(bagCostPrice)
        : null
      const sellingAmount = bagSellingPrice ? Number(bagSellingPrice) : costEur * 1.4
      const includeCny = bagIsPreorder || bagSellingCurrency === 'CNY'

      if (includeCny && !isValidRate(usdToCnyRate)) {
        setBagError('USD to CNY exchange rate is not available yet. Please try again in a moment.')
        setBagPosting(false)
        return
      }
      if (!isValidRate(rate)) {
        setBagError('EUR to USD exchange rate is not available yet. Please try again in a moment.')
        setBagPosting(false)
        return
      }

      const converted = convertBagSellingPrices(sellingAmount, bagSellingCurrency, includeCny)
      if (!converted) {
        setBagError('Could not calculate converted prices. Please check the amount and exchange rates.')
        setBagPosting(false)
        return
      }

      const { priceEur, priceUsd, priceCny } = converted
      const requiredPrices = bagIsPreorder ? [priceEur, priceUsd, priceCny] : [priceEur, priceUsd]
      if (!requiredPrices.every(v => Number.isFinite(v) && v > 0)) {
        setBagError('Could not calculate valid converted prices. Please check the amount and exchange rates.')
        setBagPosting(false)
        return
      }

      const payload = {
        category: bagCategory,
        brand: bagBrand,
        model: bagModel.trim(),
        reference: null,
        condition: bagCondition,
        price_eur: Math.round(priceEur),
        price_usd: Math.round(priceUsd),
        cost_eur: costEur === null ? null : Math.round(costEur),
        vendor: parsed.vendor || null,
        notes: parsed.notes || null,
        scope_of_delivery: null,
        metal_type: null,
        subcategory: null,
        item_size: null,
        posted_by: profile.id,
        status: 'available',
      }

      const table = bagIsPreorder ? 'preorders' : 'products'
      const imgTable = bagIsPreorder ? 'preorder_images' : 'product_images'
      const fkCol = bagIsPreorder ? 'preorder_id' : 'product_id'
      const insertPayload = bagIsPreorder
        ? { ...payload, price_cny: Math.round(priceCny), price_currency: bagSellingCurrency }
        : { ...payload, source: 'manual' }

      const { data: item, error: pErr } = await supabase.from(table).insert(insertPayload).select().single()
      if (pErr) throw pErr

      let imagesFailed = 0
      let bagImgPos = 0
      const imageUrls = []
      for (let i = 0; i < bagImages.length; i++) {
        const file = bagImages[i]
        const ext = file.name.split('.').pop()
        const path = `${item.id}/${i}.${ext}`
        const { error: upErr } = await supabase.storage.from('watch-images').upload(path, file)
        if (upErr) { console.error('Bag image upload error:', upErr.message); imagesFailed++; continue }
        const { data: { publicUrl } } = supabase.storage.from('watch-images').getPublicUrl(path)
        imageUrls.push(publicUrl)
        const { error: dbErr } = await supabase.from(imgTable).insert({ [fkCol]: item.id, url: publicUrl, position: bagImgPos })
        if (dbErr) { console.error('Bag image DB error:', dbErr.message); imagesFailed++ }
        else bagImgPos++
      }

      if (bagIsPreorder && bagCategory === 'Bags' && imageUrls.length > 0) {
        notifyN8nBagPreorder(item, imageUrls)
      }

      setBagName('')
      setBagCategory('Bags')
      setBagBrand('Other')
      setBagModel('')
      setBagCondition('Preowned')
      setBagCostPrice('')
      setBagCostCurrency('EUR')
      setBagSellingPrice('')
      setBagSellingCurrency('EUR')
      setBagImages([])
      setBagPreviews([])
      setBagIsPreorder(false)
      setBagMsg(imagesFailed > 0
        ? `Bag ${bagIsPreorder ? 'preorder' : 'listing'} posted, but ${imagesFailed} image(s) failed to save — open the listing and re-upload them.`
        : bagIsPreorder ? 'Bags preorder posted.' : 'Item posted — now live in the dealer catalog.')
      setTab('listings')
      setListingType(bagIsPreorder ? 'preorders-bags' : 'instock')
      fetchPreorders(0, listingType, search)
      fetchMyWatches()
    } catch (err) {
      console.error('Bag listing post error:', err)
      setBagError(err?.message || 'Something went wrong. Please try again.')
    }
    setBagPosting(false)
  }

  async function handleAcceptOffer(offer) {
    if (agentComments[offer.id]) {
      await supabase.from('offers').update({ agent_comment: agentComments[offer.id] }).eq('id', offer.id)
    }
    const { error } = await supabase.rpc('accept_offer', { offer_id: offer.id })
    if (error) console.error('accept_offer error:', error)
    notifyOffer({
      action: 'accepted',
      watch: offer.products,
      dealer_whatsapp: offer.dealer_whatsapp,
      offer_price: offer.offer_price,
      agent_comment: agentComments[offer.id] || null,
    })
    setMsg(`Offer accepted for ${offer.products.brand} ${offer.products.model}.`)
    fetchOffers()
  }

  async function handleRejectOffer(offer) {
    await supabase.from('offers').update({ status: 'rejected', agent_comment: agentComments[offer.id] || null, updated_at: new Date().toISOString() }).eq('id', offer.id)
    notifyOffer({
      action: 'rejected',
      watch: offer.products,
      dealer_whatsapp: offer.dealer_whatsapp,
      offer_price: offer.offer_price,
      agent_comment: agentComments[offer.id] || null,
    })
    setMsg(`Offer rejected for ${offer.products.brand} ${offer.products.model}.`)
    fetchOffers()
  }

  async function handleCounterOffer(offer) {
    const counterPrice = counterInputs[offer.id]
    if (!counterPrice) return
    await supabase.from('offers').update({
      status: 'countered',
      counter_price: Number(counterPrice),
      agent_comment: agentComments[offer.id] || null,
      updated_at: new Date().toISOString()
    }).eq('id', offer.id)
    notifyOffer({
      action: 'countered',
      watch: offer.products,
      dealer_whatsapp: offer.dealer_whatsapp,
      dealer_name: 'Dealer',
      counter_price: Number(counterPrice),
      agent_comment: agentComments[offer.id] || null,
    })
    setMsg(`Counter offer sent for ${offer.products.brand} ${offer.products.model}.`)
    setCounterOpen(prev => ({ ...prev, [offer.id]: false }))
    fetchOffers()
  }

  async function markSold(product) {
    await supabase.from('products').update({ status: 'sold' }).eq('id', product.id)
    fetchMyWatches()
    // Note: regular products already exist in Zoho — no draft creation needed
  }

  async function markPreorderSold(preorder) {
    await supabase.from('preorders').update({ status: 'sold' }).eq('id', preorder.id)
    fetchPreorders(0, listingType, search)
    setMsg('Marked as sold.')
    // Zoho draft creation temporarily disabled
    // fetch('/api/zoho-create-draft', { ... })
  }

  async function markPreorderAvailable(id) {
    await supabase.from('preorders').update({ status: 'available' }).eq('id', id)
    fetchPreorders(0, listingType, search)
  }

  // Extend an active preorder by 7 more days, or bring an archived one back —
  // identical operation, since "reactivating" just means giving it a future expiry again.
  async function extendPreorder(id) {
    await supabase.from('preorders').update({ expires_at: new Date(Date.now() + 7 * 86400000).toISOString() }).eq('id', id)
    fetchPreorders(0, listingType, search)
  }

  async function requestPreorderRepost(preorder) {
    if (!profile?.id || repostingId) return
    setRepostingId(preorder.id)
    const { data: request, error } = await supabase
      .from('repost_requests')
      .insert({ preorder_id: preorder.id, requested_by: profile.id })
      .select('id, preorder_id, status, requested_at, completed_at, error_message')
      .single()

    if (error) {
      console.error('request repost error:', error)
      const isDuplicatePending = error.code === '23505'
      setMsg(isDuplicatePending ? 'Repost already pending.' : `Could not request repost: ${error.message}`)
    } else {
      setPreorderReposts(prev => ({ ...prev, [preorder.id]: request }))
      setMsg(`Repost requested for ${preorder.brand} ${preorder.model}.`)
    }
    setRepostingId(null)
  }

  function repostStatusText(repost) {
    if (!repost) return null
    if (repost.status === 'pending') return `Repost pending since ${fmtDate(repost.requested_at)}`
    if (repost.status === 'completed') return `Last reposted ${fmtDate(repost.completed_at || repost.requested_at)}`
    if (repost.status === 'failed') return `Repost failed ${fmtDate(repost.requested_at)}`
    return null
  }

  function daysUntilExpiry(expiresAt) {
    if (!expiresAt) return null
    return Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86400000)
  }

  function fmtDate(iso) {
    if (!iso) return ''
    const d = new Date(iso)
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const yy = String(d.getFullYear()).slice(2)
    return `${dd}/${mm}/${yy}`
  }

  function getPreorderThumb(p) {
    const imgs = [...(p.preorder_images || [])].sort((a, b) => a.position - b.position)
    return imgs[0]?.url || null
  }

  async function setMainImage(preorderId, targetImg, allImgs) {
    const sorted = [...allImgs].sort((a, b) => a.position - b.position)
    const currentMain = sorted[0]
    if (currentMain.id === targetImg.id) return // already main
    // Swap positions — two-pass to avoid UNIQUE(preorder_id, position) conflicts
    await Promise.all([
      supabase.from('preorder_images').update({ position: 10000 }).eq('id', targetImg.id),
      supabase.from('preorder_images').update({ position: 10001 }).eq('id', currentMain.id),
    ])
    await Promise.all([
      supabase.from('preorder_images').update({ position: currentMain.position }).eq('id', targetImg.id),
      supabase.from('preorder_images').update({ position: targetImg.position }).eq('id', currentMain.id),
    ])
    // Update local state so UI reflects change immediately
    setPreorders(prev => prev.map(p => {
      if (p.id !== preorderId) return p
      return {
        ...p,
        preorder_images: p.preorder_images.map(img => {
          if (img.id === targetImg.id) return { ...img, position: currentMain.position }
          if (img.id === currentMain.id) return { ...img, position: targetImg.position }
          return img
        })
      }
    }))
  }

  async function deletePreorder(id) {
    if (!window.confirm('Delete this preorder?')) return
    await supabase.from('preorder_images').delete().eq('preorder_id', id)
    const { error } = await supabase.from('preorders').delete().eq('id', id)
    if (error) { alert('Delete failed: ' + error.message); return }
    fetchPreorders(0, listingType, search)
  }

  async function deleteWatch(id) {
    if (!window.confirm('Delete this item?')) return
    const { error } = await supabase.from('products').delete().eq('id', id)
    if (error) { alert('Delete failed: ' + error.message); return }
    fetchMyWatches()
  }

  function fmtPrice(w) {
    if (currency === 'USD') {
      if (w.price_usd) return '$' + Number(w.price_usd).toLocaleString()
      if (w.price_eur && rate) return '$' + Math.round(Number(w.price_eur) * rate).toLocaleString()
      return '—'
    }
    if (w.price_eur) return '€' + Number(w.price_eur).toLocaleString()
    return '—'
  }

  function getThumb(w) {
    const imgs = [...(w.product_images || [])].sort((a, b) => a.position - b.position)
    return imgs[0]?.url || null
  }

  const filteredWatches = watches.filter(w => {
    // search is server-side; only apply local filters here
    if (inStockBrand !== 'all' && w.brand !== inStockBrand) return false
    if (inStockCondition !== 'all' && w.condition !== inStockCondition) return false
    if (inStockStatus !== 'all' && w.status !== inStockStatus) return false
    return true
  }).sort((a, b) => {
    if (inStockSortBy === 'oldest') return new Date(a.created_at) - new Date(b.created_at)
    if (inStockSortBy === 'price_asc') return Number(a.price_eur || 0) - Number(b.price_eur || 0)
    if (inStockSortBy === 'price_desc') return Number(b.price_eur || 0) - Number(a.price_eur || 0)
    return new Date(b.created_at) - new Date(a.created_at)
  })
  // Bags always count as archived for this agent-side grouping (the Preorders Bags
  // tab below still shows all of them unaffected — this only changes which bucket
  // they fall into for the Archived tab). Watches archive normally after 7 days.
  // Server already filters by tab type — basePreorders is the fetched set for current tab
  const basePreorders = preorders
  const preorderBrandOptions = [...new Set(preorders.map(p => p.brand).filter(Boolean))].sort()
  const filteredPreorders = basePreorders.filter(p => {
    // search is server-side; only apply local filters here
    if (dateFrom && new Date(p.created_at) < new Date(dateFrom)) return false
    if (dateTo && new Date(p.created_at) > new Date(dateTo + 'T23:59:59')) return false
    if (preorderStatusFilter === 'available' && p.status !== 'available') return false
    if (preorderStatusFilter === 'sold' && p.status !== 'sold') return false
    if (preorderStatusFilter === 'expiring') {
      const days = daysUntilExpiry(p.expires_at)
      if (days === null || days > 2 || days <= 0) return false
    }
    if (preorderBrandFilter !== 'all' && p.brand !== preorderBrandFilter) return false
    if (preorderPriceMin && Number(p.price_eur || 0) < Number(preorderPriceMin)) return false
    if (preorderPriceMax && Number(p.price_eur || 0) > Number(preorderPriceMax)) return false
    return true
  }).sort((a, b) => {
    if (preorderSort === 'price_asc') return Number(a.price_eur || 0) - Number(b.price_eur || 0)
    if (preorderSort === 'price_desc') return Number(b.price_eur || 0) - Number(a.price_eur || 0)
    if (preorderSort === 'expiring') return new Date(a.expires_at || 0) - new Date(b.expires_at || 0)
    return new Date(b.created_at || 0) - new Date(a.created_at || 0)
  })

  const pendingOffers = offers.filter(o => o.status === 'pending').length

  const sbItem = (id, label, icon, count, accent) => {
    const isActive = tab === id
    return (
      <button key={id} onClick={() => setTab(id)} style={{
        display: 'flex', alignItems: 'center', gap: 10,
        width: '100%', padding: '8px 16px',
        borderTop: 'none', borderRight: 'none', borderBottom: 'none',
        borderLeft: isActive ? '2px solid #b8965a' : '2px solid transparent',
        cursor: 'pointer', textAlign: 'left',
        background: isActive ? '#faf6f0' : 'transparent',
        color: isActive ? '#b8965a' : '#374151',
        fontWeight: isActive ? 600 : 400, fontSize: 14,
        transition: 'background 0.12s, color 0.12s',
      }}>
        <span style={{ color: isActive ? '#b8965a' : '#6b7280', display: 'flex', flexShrink: 0 }}>{icon}</span>
        <span style={{ flex: 1 }}>{label}</span>
        {count > 0 && (
          <span style={{ background: accent ? '#f59e0b' : '#f3f4f6', color: accent ? '#fff' : '#6b7280', borderRadius: 10, fontSize: 11, fontWeight: 700, padding: '1px 7px' }}>{count}</span>
        )}
      </button>
    )
  }

  const sbSubItem = (lType, label, icon, count) => {
    const isActive = tab === 'listings' && listingType === lType
    return (
      <button key={lType} onClick={() => { setTab('listings'); setListingType(lType) }} style={{
        display: 'flex', alignItems: 'center', gap: 10,
        width: '100%', padding: '6px 16px',
        border: 'none', cursor: 'pointer', textAlign: 'left', background: 'transparent',
        color: isActive ? '#374151' : '#6b7280',
        fontWeight: isActive ? 500 : 400, fontSize: 13,
        transition: 'color 0.12s',
      }}>
        <span style={{ color: '#9ca3af', display: 'flex', flexShrink: 0 }}>{icon}</span>
        <span style={{ flex: 1 }}>{label}</span>
        <span style={{ fontSize: 12, color: '#9ca3af' }}>{count > 0 ? count : '—'}</span>
      </button>
    )
  }

  const IconBag = () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" viewBox="0 0 24 24"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
  const IconWatch = () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.7" viewBox="0 0 24 24"><circle cx="12" cy="12" r="7"/><polyline points="12 9 12 12 13.5 13.5"/><path d="M16.51 17.35l-.35 3.83a2 2 0 0 1-2 1.82H9.83a2 2 0 0 1-2-1.82l-.35-3.83m.01-10.7l.35-3.83A2 2 0 0 1 9.83 1h4.35a2 2 0 0 1 2 1.82l.35 3.83"/></svg>
  const IconHandbag = () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.7" viewBox="0 0 24 24"><path d="M3 6h18l-2 13H5L3 6z"/><path d="M8 6V4a4 4 0 0 1 8 0v2"/></svg>
  const IconArchive = () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.7" viewBox="0 0 24 24"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
  const IconTag = () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" viewBox="0 0 24 24"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
  const IconPerson = () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" viewBox="0 0 24 24"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
  const IconTruck = () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" viewBox="0 0 24 24"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
  const IconPlus = () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
  const IconRates = () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" viewBox="0 0 24 24"><path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>

  const selStyle = { fontSize: 13, padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border-light)', background: 'var(--surface2)', color: 'var(--text)', cursor: 'pointer' }

  return (
    <div className="page" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Topbar />

      {/* Lightbox */}
      {lbIdx !== null && (
        <div onClick={closeLb} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, cursor: 'zoom-out' }}>
          <img src={lbImgs[lbIdx]} alt="" style={{ maxWidth: '82vw', maxHeight: '88vh', objectFit: 'contain', borderRadius: 10, userSelect: 'none' }} />
          {lbImgs.length > 1 && <button onClick={lbPrev} style={{ position: 'absolute', left: 18, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: '50%', width: 44, height: 44, fontSize: 24, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>}
          {lbImgs.length > 1 && <button onClick={lbNext} style={{ position: 'absolute', right: 18, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: '50%', width: 44, height: 44, fontSize: 24, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>}
          <button onClick={closeLb} style={{ position: 'absolute', top: 18, right: 22, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: '50%', width: 38, height: 38, fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
          {lbImgs.length > 1 && <div style={{ position: 'absolute', bottom: 18, left: '50%', transform: 'translateX(-50%)', color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>{lbIdx + 1} / {lbImgs.length}</div>}
        </div>
      )}

      <div style={{ display: 'flex', flex: 1, alignItems: 'flex-start' }}>

        {/* Sidebar */}
        <aside style={{
          width: 230, flexShrink: 0,
          borderRight: '1px solid #e5e7eb',
          background: '#fff',
          display: 'flex', flexDirection: 'column',
          padding: '20px 0 16px',
          position: 'sticky', top: 0,
          height: '100vh', overflowY: 'auto',
        }}>
          {/* LISTINGS label */}
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9ca3af', padding: '0 16px', marginBottom: 6 }}>Listings</div>

          {sbItem('listings', 'My Listings', <IconBag />)}

          {/* In stock pill sub-item */}
          <button onClick={() => { setTab('listings'); setListingType('instock') }} style={{ display: 'block', width: '100%', padding: '3px 16px 3px 42px', border: 'none', cursor: 'pointer', textAlign: 'left', background: 'transparent' }}>
            <span style={{ display: 'inline-block', padding: '4px 14px', borderRadius: 20, fontSize: 13, fontWeight: tab === 'listings' && listingType === 'instock' ? 500 : 400, background: tab === 'listings' && listingType === 'instock' ? '#f5ede0' : 'transparent', color: tab === 'listings' && listingType === 'instock' ? '#374151' : '#6b7280' }}>In stock</span>
          </button>

          {sbSubItem('preorders-watches', 'Preorders Watches', <IconWatch />, watchPreordersTotal)}
          {sbSubItem('preorders-jewellery', 'Preorders Jewellery', <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.7" viewBox="0 0 24 24"><polygon points="12 2 22 8.5 22 15.5 12 22 2 15.5 2 8.5 12 2"/><line x1="12" y1="2" x2="12" y2="22"/><line x1="2" y1="8.5" x2="22" y2="8.5"/></svg>, jewelleryPreordersTotal)}
          {sbSubItem('preorders-bags', 'Preorders Bags', <IconHandbag />, bagPreordersTotal)}
          {sbSubItem('preorders-archived', 'Archived', <IconArchive />, archivedPreordersTotal)}

          <div style={{ height: 1, background: '#e5e7eb', margin: '10px 16px' }} />

          {sbItem('post', 'Post a Watch', <IconWatch />)}
          {sbItem('bagpreorder', 'Post a Bag', <IconHandbag />)}

          <div style={{ height: 1, background: '#e5e7eb', margin: '10px 16px' }} />

          {/* ACTIVITY label */}
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9ca3af', padding: '0 16px', marginBottom: 6 }}>Activity</div>

          {sbItem('offers', 'Offers', <IconTag />, pendingOffers)}
          {sbItem('clients', 'Clients', <IconPerson />)}
          {sbItem('supplier', profile?.role === 'jewellery_agent' ? 'Jewellery Queue' : 'Supplier Queue', <IconTruck />, supplierListings.length, supplierListings.length > 0)}
          {sbItem('rates', 'Exchange Rates', <IconRates />)}
          {['agent', 'admin'].includes(profile?.role) && sbItem('invite', 'Invite User', <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.7" viewBox="0 0 24 24"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="16" y1="11" x2="22" y2="11"/></svg>)}

          <div style={{ flex: 1 }} />

          {/* Need help? */}
          <div style={{ margin: '16px 12px 0', background: '#fdf8f2', border: '1px solid #e9d8bc', borderRadius: 12, padding: '14px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <div style={{ width: 32, height: 32, borderRadius: 50, background: '#f5ede0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="16" height="16" fill="none" stroke="#b8965a" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/></svg>
              </div>
              <span style={{ fontSize: 14, fontWeight: 600 }}>Need help?</span>
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12, lineHeight: 1.5 }}>Our team is here to help you.</div>
            <button style={{ width: '100%', fontSize: 13, padding: '8px 0', border: '1px solid #c8a76a', borderRadius: 8, background: 'transparent', color: '#b8965a', cursor: 'pointer', fontWeight: 500 }}>Contact Support</button>
          </div>
        </aside>

        <div style={{ flex: 1, minWidth: 0 }}>

      {tab === 'overview' && (
        <div style={{ padding: '28px 28px 40px' }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 4px' }}>Overview</h2>
          <p style={{ fontSize: 13, color: 'var(--faint)', margin: '0 0 24px' }}>Quick snapshot of your activity</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 14, marginBottom: 28 }}>
            {[
              { label: 'In Stock', value: watches.length, color: '#22c55e', tab: 'listings' },
              { label: 'Preorders', value: watchPreorders.length + bagPreorders.length, color: '#b8965a', tab: 'listings' },
              { label: 'Pending Offers', value: pendingOffers, color: '#f59e0b', tab: 'offers' },
              { label: profile?.role === 'jewellery_agent' ? 'Jewellery Queue' : 'Supplier Queue', value: supplierListings.length, color: '#6366f1', tab: 'supplier' },
            ].map(s => (
              <button key={s.label} onClick={() => setTab(s.tab)} style={{ textAlign: 'left', border: '1px solid var(--border-light)', borderRadius: 12, padding: '16px', background: 'var(--surface)', cursor: 'pointer', transition: 'border-color 0.12s' }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: s.color, marginBottom: 4 }}>{s.value}</div>
                <div style={{ fontSize: 12, color: 'var(--faint)' }}>{s.label}</div>
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn" onClick={() => setTab('post')} style={{ fontSize: 13 }}>Post a Watch</button>
            <button className="btn" onClick={() => { setTab('listings'); setListingType('instock') }} style={{ fontSize: 13 }}>View All Listings</button>
          </div>
        </div>
      )}

      {tab === 'listings' && (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Page header */}
          <div style={{ padding: '20px 28px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: '#faf6f0', border: '1px solid #e9d8bc', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="24" height="24" fill="none" stroke="#b8965a" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>My Listings</div>
              <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>Manage your listed products.</div>
            </div>
            <button onClick={() => setTab('post')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#b8965a', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <IconPlus /> Add Listing
            </button>
          </div>

          {/* Pill tabs */}
          <div style={{ padding: '14px 28px 0', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[
              { id: 'instock', label: 'In stock', count: watchesTotal ?? watches.length },
              { id: 'preorders-watches', label: 'Preorders Watches', count: watchPreordersTotal },
              { id: 'preorders-jewellery', label: 'Preorders Jewellery', count: jewelleryPreordersTotal },
              { id: 'preorders-bags', label: 'Preorders Bags', count: bagPreordersTotal },
              { id: 'preorders-archived', label: 'Archived', count: archivedPreordersTotal },
            ].map(({ id, label, count }) => (
              <button key={id} onClick={() => setListingType(id)} style={{
                padding: '6px 16px', borderRadius: 20, cursor: 'pointer', whiteSpace: 'nowrap',
                fontSize: 13, fontWeight: listingType === id ? 600 : 400,
                background: listingType === id ? '#b8965a' : '#fff',
                color: listingType === id ? '#fff' : '#6b7280',
                border: listingType === id ? '1px solid #b8965a' : '1px solid #e5e7eb',
                transition: 'background 0.12s, color 0.12s',
              }}>
                {label}{count > 0 ? ` (${count})` : ''}
              </button>
            ))}
          </div>

          <div style={{ padding: '14px 28px', flex: 1, overflowY: 'auto', minHeight: 'calc(100vh - 60px)', display: 'flex', flexDirection: 'column' }}>
          {msg && <div className="success-msg" style={{ marginBottom: 12 }}>{msg}</div>}

          {/* Search + grid/list toggle */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input placeholder="Search brand, model or reference..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', paddingLeft: 32 }} />
            </div>
            <div style={{ display: 'flex', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
              <button onClick={() => setViewMode('grid')} title="Grid view" style={{ padding: '7px 10px', border: 'none', cursor: 'pointer', background: viewMode === 'grid' ? '#f5ede0' : '#fff', color: viewMode === 'grid' ? '#b8965a' : '#6b7280', display: 'flex', alignItems: 'center' }}>
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.7" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
              </button>
              <button onClick={() => setViewMode('list')} title="List view" style={{ padding: '7px 10px', border: 'none', cursor: 'pointer', background: viewMode === 'list' ? '#f5ede0' : '#fff', color: viewMode === 'list' ? '#b8965a' : '#6b7280', display: 'flex', alignItems: 'center' }}>
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.7" viewBox="0 0 24 24"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
              </button>
            </div>
          </div>

          {(['preorders-watches','preorders-jewellery','preorders-bags','preorders-archived'].includes(listingType)) && (
            <>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--faint)', marginBottom: 4 }}>Listed from</label>
                  <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ width: '100%', fontSize: 13, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)', boxSizing: 'border-box' }} />
                </div>
                <span style={{ color: 'var(--faint)', fontSize: 13, paddingBottom: 8 }}>—</span>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--faint)', marginBottom: 4 }}>Listed to</label>
                  <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ width: '100%', fontSize: 13, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)', boxSizing: 'border-box' }} />
                </div>
                {(dateFrom || dateTo) && (
                  <button className="btn btn-sm" onClick={() => { setDateFrom(''); setDateTo('') }} style={{ whiteSpace: 'nowrap' }}>Clear</button>
                )}
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                <select value={preorderStatusFilter} onChange={e => setPreorderStatusFilter(e.target.value)} style={selStyle}>
                  <option value="all">All statuses</option>
                  <option value="available">Available</option>
                  <option value="sold">Sold</option>
                  <option value="expiring">Expiring soon</option>
                </select>
                <select value={preorderBrandFilter} onChange={e => setPreorderBrandFilter(e.target.value)} style={selStyle}>
                  <option value="all">All brands</option>
                  {preorderBrandOptions.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
                <input
                  type="number" placeholder="Min €" value={preorderPriceMin} onChange={e => setPreorderPriceMin(e.target.value)}
                  style={{ width: 90, fontSize: 13, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)' }}
                />
                <input
                  type="number" placeholder="Max €" value={preorderPriceMax} onChange={e => setPreorderPriceMax(e.target.value)}
                  style={{ width: 90, fontSize: 13, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)' }}
                />
                <select value={preorderSort} onChange={e => setPreorderSort(e.target.value)} style={selStyle}>
                  <option value="newest">Newest</option>
                  <option value="price_asc">Price ↑</option>
                  <option value="price_desc">Price ↓</option>
                  <option value="expiring">Expiring soon</option>
                </select>
              </div>
            </>
          )}

          {listingType === 'instock' && (
            loading
              ? <div className="loading-page" style={{ minHeight: 200 }}><div className="spinner" /></div>
              : filteredWatches.length === 0
                ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', color: 'var(--faint)' }}>
                    <svg width="64" height="64" fill="none" stroke="currentColor" strokeWidth="1.2" viewBox="0 0 24 24" style={{ marginBottom: 16, opacity: 0.35 }}>
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                      <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                      <line x1="12" y1="22.08" x2="12" y2="12"/>
                    </svg>
                    <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, color: '#1f2937' }}>{search ? 'No results found' : 'No items posted yet'}</div>
                    <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>{search ? 'Try a different search or filter' : 'Start adding products to your listings.'}</div>
                    {!search && <button onClick={() => setTab('post')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#b8965a', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}><IconPlus /> Add your first listing</button>}
                  </div>
                )
                : filteredWatches.map(w => (
                <div key={w.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', border: '1px solid var(--border-light)', borderRadius: 10, marginBottom: 8, background: 'var(--surface)' }}>
                  <a href={`/catalog/${toSlug(w)}`} onClick={e => { if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return; e.preventDefault(); navigate(`/catalog/${toSlug(w)}`) }} style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, textDecoration: 'none', color: 'inherit', minWidth: 0 }}>
                    <div style={{ width: 50, height: 50, borderRadius: 8, background: 'var(--surface2)', border: '1px solid var(--border)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {getThumb(w) ? <img src={getThumb(w)} alt={w.model} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 20 }}>⌚</span>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{w.brand} {w.model}</div>
                      <div style={{ fontSize: 11, color: '#aaa' }}>{fmtPrice(w)} · {w.condition}{w.reference ? ` · ${w.reference}` : ''}{w.category ? ` · ${w.category}` : ''}</div>
                    </div>
                  </a>
                  <span className={`badge badge-${w.status}`}>{w.status}</span>
                  <button className="btn btn-sm" onClick={() => navigate(`/catalog/${toSlug(w)}`)}>Edit</button>
                  {w.status !== 'sold' && (
                    <button className="btn btn-sm" onClick={() => markSold(w)}>Mark sold</button>
                  )}
                  {(profile?.role === 'admin' || w.posted_by === profile?.id) && (
                    <button className="btn btn-sm btn-danger" onClick={() => deleteWatch(w.id)}>Delete</button>
                  )}
                </div>
              ))
          )}

          {listingType === 'instock' && !loading && watchesTotal !== null && watches.length < watchesTotal && (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <button className="btn btn-sm" onClick={() => fetchMyWatches(watchesPage + 1, search)}>
                Load more — showing {watches.length} of {watchesTotal}
              </button>
            </div>
          )}

          {(['preorders-watches','preorders-jewellery','preorders-bags','preorders-archived'].includes(listingType)) && (
            filteredPreorders.length === 0
              ? <div className="empty-state">{search ? 'No preorders match your search' : listingType === 'preorders-archived' ? 'No archived preorders' : 'No preorders yet'}</div>
              : filteredPreorders.map(p => {
              const days = daysUntilExpiry(p.expires_at)
              const archived = listingType === 'preorders-archived'
              return (
              <div key={p.id} style={{ border: '1px solid var(--border-light)', borderRadius: 10, marginBottom: 8, background: 'var(--surface)', overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px' }}>
                  <a href={`/catalog/${toSlug(p)}`} onClick={e => { if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return; e.preventDefault(); navigate(`/catalog/${toSlug(p)}`) }} style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, textDecoration: 'none', color: 'inherit', minWidth: 0 }}>
                    <div style={{ width: 50, height: 50, borderRadius: 8, background: 'var(--surface2)', border: '1px solid var(--border)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {getPreorderThumb(p) ? <img src={getPreorderThumb(p)} alt={p.model} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 20 }}>🔖</span>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{p.brand} {p.model}</div>
                      <div style={{ fontSize: 11, color: '#aaa' }}>{p.price_eur ? `€${Number(p.price_eur).toLocaleString()}` : '—'} · {p.condition}{p.category ? ` · ${p.category}` : ''}</div>
                      {listingType === 'preorders-bags' && repostStatusText(preorderReposts[p.id]) && (
                        <div style={{ fontSize: 10, color: preorderReposts[p.id]?.status === 'failed' ? '#c62828' : '#b8965a', marginTop: 2 }}>
                          {repostStatusText(preorderReposts[p.id])}
                        </div>
                      )}
                    </div>
                  </a>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                    <span className={`badge badge-${p.status}`}>{p.status}</span>
                    {!archived && days !== null && (
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 8,
                        color: days <= 2 ? '#fff' : '#888',
                        background: days <= 2 ? '#d9534f' : 'var(--surface2)',
                      }}>
                        Expires in {days}d
                      </span>
                    )}
                    {archived && (
                      <span style={{ fontSize: 10, color: '#bbb' }}>Expired {fmtDate(p.expires_at)}</span>
                    )}
                    {p.status === 'posted' && p.posted_at && (
                      <span style={{ fontSize: 10, color: '#bbb' }}>Posted {fmtDate(p.posted_at)}</span>
                    )}
                  </div>
                  {p.preorder_images?.length > 0 && (
                    <button className="btn btn-sm" onClick={e => { e.stopPropagation(); setExpandedImgPreorder(prev => prev === p.id ? null : p.id) }}
                      style={{ fontSize: 11, color: expandedImgPreorder === p.id ? '#b8965a' : undefined }}>
                      🖼 Images ({p.preorder_images.length})
                    </button>
                  )}
                  {p.status === 'sold'
                    ? <button className="btn btn-sm" onClick={e => { e.stopPropagation(); markPreorderAvailable(p.id) }}>Mark available</button>
                    : <button className="btn btn-sm" onClick={e => { e.stopPropagation(); markPreorderSold(p) }}>Mark sold</button>
                  }
                  <button className="btn btn-sm" onClick={e => { e.stopPropagation(); extendPreorder(p.id) }}>
                    {archived ? 'Reactivate' : 'Extend 7 days'}
                  </button>
                  {listingType === 'preorders-bags' && (profile?.role === 'admin' || p.posted_by === profile?.id) && (
                    <button
                      className="btn btn-sm"
                      onClick={e => { e.stopPropagation(); requestPreorderRepost(p) }}
                      disabled={preorderReposts[p.id]?.status === 'pending' || repostingId === p.id}
                    >
                      {repostingId === p.id ? 'Requesting…' : preorderReposts[p.id]?.status === 'pending' ? 'Repost pending' : 'Repost'}
                    </button>
                  )}
                  {(profile?.role === 'admin' || p.posted_by === profile?.id) && (
                    <button className="btn btn-sm btn-danger" onClick={e => { e.stopPropagation(); deletePreorder(p.id) }}>Delete</button>
                  )}
                </div>

                {/* Expandable image manager */}
                {expandedImgPreorder === p.id && (() => {
                  const imgs = [...(p.preorder_images || [])].sort((a, b) => a.position - b.position)
                  return (
                    <div style={{ padding: '10px 14px 14px', borderTop: '1px solid var(--border-light)', background: 'var(--surface2)' }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 10 }}>Images — click any to set as main</div>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        {imgs.map((img, i) => {
                          const isMain = i === 0
                          return (
                            <div key={img.id} style={{ position: 'relative', cursor: isMain ? 'default' : 'pointer' }}
                              onClick={() => !isMain && setMainImage(p.id, img, imgs)}>
                              <img src={img.url} alt="" style={{
                                width: 80, height: 80, objectFit: 'cover', borderRadius: 8,
                                border: isMain ? '2px solid #b8965a' : '2px solid transparent',
                                opacity: isMain ? 1 : 0.75,
                                transition: 'opacity 0.15s, border 0.15s',
                              }}
                              onMouseEnter={e => { if (!isMain) e.currentTarget.style.opacity = '1' }}
                              onMouseLeave={e => { if (!isMain) e.currentTarget.style.opacity = '0.75' }}
                              />
                              {isMain && (
                                <div style={{ position: 'absolute', bottom: 4, left: 0, right: 0, textAlign: 'center' }}>
                                  <span style={{ background: '#b8965a', color: '#fff', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, letterSpacing: '0.05em' }}>MAIN</span>
                                </div>
                              )}
                              {!isMain && (
                                <div style={{ position: 'absolute', bottom: 4, left: 0, right: 0, textAlign: 'center', opacity: 0 }}
                                  className="set-main-hint">
                                  <span style={{ background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 9, fontWeight: 600, padding: '2px 5px', borderRadius: 4 }}>Set main</span>
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )
                })()}
              </div>
              )
            })
          )}

          {(['preorders-watches','preorders-jewellery','preorders-bags','preorders-archived'].includes(listingType)) && preordersTotal !== null && preorders.length < preordersTotal && (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <button className="btn btn-sm" onClick={() => fetchPreorders(preordersPage + 1, listingType, search)}>
                Load more — showing {preorders.length} of {preordersTotal}
              </button>
            </div>
          )}
          </div>
        </div>
      )}

      {tab === 'offers' && (
        <div style={{ padding: 16, maxWidth: 700 }}>
          {msg && <div className="success-msg" style={{ marginBottom: 12 }}>{msg}</div>}
          <div className="tabs" style={{ marginBottom: 16 }}>
            {['pending', 'countered', 'accepted', 'rejected'].map(s => {
              const count = offers.filter(o => o.status === s).length
              const badgeColor = s === 'pending' ? '#e6a817' : s === 'countered' ? '#b8965a' : s === 'accepted' ? '#2e7d32' : '#c62828'
              return (
                <div key={s} className={`tab ${offerStatusTab === s ? 'active' : ''}`} onClick={() => setOfferStatusTab(s)} style={{ textTransform: 'capitalize' }}>
                  {s}{count > 0 && <span style={{ marginLeft: 5, background: badgeColor, color: '#fff', borderRadius: 10, fontSize: 10, padding: '1px 6px', fontWeight: 700 }}>{count}</span>}
                </div>
              )
            })}
          </div>
          {offersLoading
            ? <div className="loading-page" style={{ minHeight: 200 }}><div className="spinner" /></div>
            : offers.filter(o => o.status === offerStatusTab).length === 0
              ? <div className="empty-state">No {offerStatusTab} offers</div>
              : offers.filter(o => o.status === offerStatusTab).map(offer => {
                const watch = offer.products
                const imgs = [...(watch?.product_images || [])].sort((a, b) => a.position - b.position)
                const thumb = imgs[0]?.url || null
                const STATUS_COLOR = { pending: '#e6a817', countered: '#b8965a', accepted: '#2e7d32', rejected: '#c62828' }
                return (
                  <div key={offer.id} style={{ border: '1px solid var(--border-light)', borderRadius: 12, padding: 16, marginBottom: 12, background: 'var(--surface)' }}>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      <div
                        onClick={() => navigate(`/catalog/${toSlug(watch)}`)}
                        style={{ width: 52, height: 52, borderRadius: 8, background: 'var(--surface2)', border: '1px solid var(--border)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer' }}
                      >
                        {thumb ? <img src={thumb} alt={watch.model} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 20 }}>⌚</span>}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 14, cursor: 'pointer' }} onClick={() => navigate(`/catalog/${toSlug(watch)}`)}>
                              {watch.brand} {watch.model}
                            </div>
                            <div style={{ fontSize: 11, color: '#aaa' }}>{offer.dealer_whatsapp ? `WA: ${offer.dealer_whatsapp}` : 'Dealer'}</div>
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 700, color: STATUS_COLOR[offer.status] || '#888', textTransform: 'uppercase', letterSpacing: '0.5px', flexShrink: 0 }}>
                            {offer.status}
                          </span>
                        </div>

                        <div style={{ marginTop: 10, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                          <div>
                            <div style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Listing price</div>
                            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--muted)' }}>{watch?.price_eur ? `€${Number(watch.price_eur).toLocaleString()}` : '—'}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Offer</div>
                            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>€{Number(offer.offer_price).toLocaleString()}</div>
                          </div>
                          {offer.counter_price && (
                            <div>
                              <div style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Your counter</div>
                              <div style={{ fontSize: 15, fontWeight: 600, color: '#b8965a' }}>€{Number(offer.counter_price).toLocaleString()}</div>
                            </div>
                          )}
                        </div>

                        {offer.dealer_comment && (
                          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)', background: 'var(--surface2)', borderRadius: 6, padding: '6px 10px' }}>
                            <span style={{ color: 'var(--faint)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Dealer · </span>
                            {offer.dealer_comment}
                          </div>
                        )}
                        {offer.agent_comment && (
                          <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-muted)', background: 'rgba(184,150,106,0.08)', borderRadius: 6, padding: '6px 10px' }}>
                            <span style={{ color: 'var(--faint)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Your note · </span>
                            {offer.agent_comment}
                          </div>
                        )}

                        {(offer.status === 'pending' || offer.status === 'countered') && (
                          <div style={{ marginTop: 10 }}>
                            <div className="form-row" style={{ marginBottom: 6 }}>
                              <input
                                placeholder="Add a note (optional)"
                                value={agentComments[offer.id] || ''}
                                onChange={e => setAgentComments(prev => ({ ...prev, [offer.id]: e.target.value }))}
                                style={{ fontSize: 12 }}
                              />
                            </div>
                            {counterOpen[offer.id] ? (
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                <input
                                  type="number"
                                  placeholder="Counter price (€)"
                                  value={counterInputs[offer.id] || ''}
                                  onChange={e => setCounterInputs(prev => ({ ...prev, [offer.id]: e.target.value }))}
                                  style={{ width: 160, fontSize: 13 }}
                                />
                                <button className="btn btn-sm btn-dark" onClick={() => handleCounterOffer(offer)} disabled={!counterInputs[offer.id]}>Send Counter</button>
                                <button className="btn btn-sm" onClick={() => setCounterOpen(prev => ({ ...prev, [offer.id]: false }))}>Cancel</button>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <button className="btn btn-sm btn-green" onClick={() => handleAcceptOffer(offer)}>Accept</button>
                                <button className="btn btn-sm" style={{ color: '#c00', borderColor: '#f09595' }} onClick={() => handleRejectOffer(offer)}>Reject</button>
                                <button className="btn btn-sm" onClick={() => setCounterOpen(prev => ({ ...prev, [offer.id]: true }))}>Counter</button>
                              </div>
                            )}
                          </div>
                        )}

                        <div style={{ marginTop: 8, fontSize: 10, color: '#bbb' }}>
                          {new Date(offer.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          {offer.dealer_whatsapp && <span> · WA: {offer.dealer_whatsapp}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })
          }
        </div>
      )}

      {tab === 'post' && (
        <div style={{ padding: '16px 16px 40px', maxWidth: 600 }}>
          {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}

          <form onSubmit={handlePost}>

            {/* Photos */}
            <label htmlFor="img-upload" style={{ display: 'block', marginBottom: 20, borderRadius: 14, border: '1.5px dashed var(--border)', background: 'var(--surface)', cursor: 'pointer', overflow: 'hidden', minHeight: 90, transition: 'border-color 0.15s' }}>
              {previews.length > 0
                ? <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: 12 }}>
                    {previews.map((p, i) => <img key={i} src={p} alt="" style={{ width: 68, height: 68, borderRadius: 10, objectFit: 'cover', border: '1px solid #e8e5e0' }} />)}
                    <div style={{ width: 68, height: 68, borderRadius: 10, border: '1.5px dashed #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: 22 }}>+</div>
                  </div>
                : <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 90, gap: 6 }}>
                    <div style={{ fontSize: 24, lineHeight: 1 }}>&#128247;</div>
                    <div style={{ fontSize: 13, color: '#888', fontWeight: 500 }}>Tap to upload photos</div>
                    <div style={{ fontSize: 11, color: '#bbb' }}>JPG, PNG — multiple allowed</div>
                  </div>
              }
              <input id="img-upload" type="file" accept="image/*" multiple onChange={handleImages} style={{ display: 'none' }} />
            </label>

            {/* Quick Post */}
            <div style={{ marginBottom: 20, background: 'var(--surface)', borderRadius: 14, padding: '14px 16px', border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4, letterSpacing: '-0.1px' }}>Quick Post</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 10, lineHeight: 1.5 }}>Paste all info — brand, model, price, vendor — and the fields fill automatically.</div>
              <textarea
                rows={4}
                placeholder={'Panerai PAM00359\nCard & Box 2014\n3300\n2380\nDingsp'}
                style={{ width: '100%', boxSizing: 'border-box', fontSize: 13, borderRadius: 8, resize: 'vertical', lineHeight: 1.6 }}
                onChange={async e => {
                  const parsed = parseQuickPost(e.target.value)
                  if (parsed.reference && (!parsed.model || parsed.model === parsed.reference)) {
                    const { data } = await supabase.from('products').select('model').ilike('reference', `%${parsed.reference}%`).limit(1).single()
                    if (data?.model) parsed.model = data.model
                  }
                  setForm(f => {
                    const updated = { ...f }
                    if (parsed.brand !== EMPTY_FORM.brand || !f.brand) updated.brand = parsed.brand
                    if (parsed.model) updated.model = parsed.model
                    if (parsed.price_eur) updated.price_eur = parsed.price_eur
                    if (parsed.condition && parsed.condition !== EMPTY_FORM.condition) updated.condition = parsed.condition
                    if (parsed.category) updated.category = parsed.category
                    if (parsed.scope_of_delivery) updated.scope_of_delivery = parsed.scope_of_delivery
                    if (parsed.metal_type) updated.metal_type = parsed.metal_type
                    if (parsed.subcategory) updated.subcategory = parsed.subcategory
                    if (parsed.cost_eur) updated.cost_eur = parsed.cost_eur
                    if (parsed.vendor) updated.vendor = parsed.vendor
                    if (parsed.notes) updated.notes = parsed.notes
                    return updated
                  })
                }}
              />
            </div>

            {/* Item details */}
            <div style={{ background: 'var(--surface)', borderRadius: 14, border: '1px solid var(--border)', padding: '16px 16px 4px', marginBottom: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.9px', marginBottom: 14 }}>Item details</div>

              <div className="form-row">
                <label>Category</label>
                <select value={form.category} onChange={e => handleField('category', e.target.value)}>
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>

              <div className="form-2col">
                <div className="form-row">
                  <label>Brand</label>
                  <select value={form.brand} onChange={e => handleField('brand', e.target.value)}>
                    {BRANDS.map(b => <option key={b}>{b}</option>)}
                  </select>
                </div>
                <div className="form-row">
                  <label>Condition</label>
                  <select value={form.condition} onChange={e => handleField('condition', e.target.value)}>
                    {CONDITIONS.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <label>Model name</label>
                <input value={form.model} onChange={e => handleField('model', e.target.value)} placeholder="e.g. Daytona, Birkin, Love Bracelet" required />
              </div>

              {form.category === 'Jewellery' && (
                <>
                  <div className="form-row">
                    <label>Jewellery type</label>
                    <select value={form.subcategory} onChange={e => { handleField('subcategory', e.target.value); handleField('item_size', '') }}>
                      <option value="">Select type</option>
                      <option>Rings</option>
                      <option>Bracelets</option>
                      <option>Necklaces</option>
                      <option>Earrings</option>
                    </select>
                  </div>
                  <div className="form-row">
                    <label>Metal type</label>
                    <select value={form.metal_type} onChange={e => handleField('metal_type', e.target.value)}>
                      <option value="">Select metal</option>
                      <option>Yellow Gold</option>
                      <option>Pink Gold</option>
                      <option>White Gold</option>
                      <option>Platinum</option>
                    </select>
                  </div>
                  {form.subcategory === 'Rings' && (
                    <div className="form-row">
                      <label>Ring size</label>
                      <select value={form.item_size} onChange={e => handleField('item_size', e.target.value)}>
                        <option value="">Select size</option>
                        {['50','51','52','53','54','55','56','57','58','59','60','61','62','63','64','65'].map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                  )}
                  {form.subcategory === 'Bracelets' && (
                    <div className="form-row">
                      <label>Bracelet size</label>
                      <select value={form.item_size} onChange={e => handleField('item_size', e.target.value)}>
                        <option value="">Select size</option>
                        {['14','15','16','17','18','19','20','21','22','23','XS','S','M','L','XL','XXL','3XL'].map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                  )}
                </>
              )}

              {form.category === 'Watches' && (
                <div className="form-row">
                  <label>Scope of Delivery</label>
                  <select value={form.scope_of_delivery || ''} onChange={e => handleField('scope_of_delivery', e.target.value)} required>
                    <option value="">Select scope</option>
                    <option>Watch Only</option>
                    <option>With Card</option>
                    <option>With Box</option>
                    <option>Card & Box</option>
                  </select>
                </div>
              )}
            </div>

            {/* Pricing & info */}
            <div style={{ background: 'var(--surface)', borderRadius: 14, border: '1px solid var(--border)', padding: '16px 16px 4px', marginBottom: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.9px', marginBottom: 14 }}>Pricing & info</div>

              <div className="form-2col">
                <div className="form-row">
                  <label>Selling Price (€)</label>
                  <input type="number" value={form.price_eur} onChange={e => handleField('price_eur', e.target.value)} placeholder="e.g. 35000" required />
                  {usdPreview && <div style={{ fontSize: 12, color: '#b0a898', marginTop: 4 }}>≈ {usdPreview} USD</div>}
                </div>
                <div className="form-row">
                  <label>Cost Price (€)</label>
                  <input type="number" value={form.cost_eur} onChange={e => handleField('cost_eur', e.target.value)} placeholder="e.g. 28000" />
                </div>
              </div>

              <div className="form-row">
                <label>Vendor</label>
                <input value={form.vendor} onChange={e => handleField('vendor', e.target.value)} placeholder="e.g. c713671" />
              </div>

              <div className="form-row">
                <label>Notes</label>
                <textarea value={form.notes} onChange={e => handleField('notes', e.target.value)} rows={3} placeholder="Box & papers, year, condition details..." />
              </div>
            </div>

            {/* Preorder toggle */}
            <div
              onClick={() => handleField('is_preorder', !form.is_preorder)}
              style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: form.is_preorder ? 'var(--gold-light)' : 'var(--surface)', borderRadius: 14, border: `1px solid ${form.is_preorder ? 'rgba(184,150,106,0.4)' : 'var(--border)'}`, marginBottom: 20, cursor: 'pointer', transition: 'all 0.2s' }}
            >
              <div style={{ width: 44, height: 26, borderRadius: 13, background: form.is_preorder ? '#b8965a' : '#ddd', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                <div style={{ position: 'absolute', top: 3, left: form.is_preorder ? 21 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.18s', boxShadow: '0 1px 4px rgba(0,0,0,0.18)' }} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: form.is_preorder ? 'var(--gold)' : 'var(--text)' }}>Preorder</div>
                <div style={{ fontSize: 11, color: '#b0a898', marginTop: 1 }}>{form.is_preorder ? 'No SKU required — item not yet in stock' : 'Toggle on if item is not yet in stock'}</div>
              </div>
            </div>

            <button type="submit" className="btn btn-dark btn-full" disabled={posting} style={{ height: 48, fontSize: 15, borderRadius: 12 }}>
              {posting ? <span className="spinner" style={{ width: 18, height: 18 }} /> : form.is_preorder ? 'Post preorder' : 'Post to catalog'}
            </button>
          </form>
        </div>
      )}

      {tab === 'bagpreorder' && (
        <div style={{ padding: '16px 16px 40px', maxWidth: 600 }}>
          {bagMsg && <div className="success-msg" style={{ marginBottom: 16 }}>{bagMsg}</div>}
          {bagError && <div className="error-msg" style={{ marginBottom: 16 }}>{bagError}</div>}

          <form onSubmit={handleBagPost}>
            {/* Photos */}
            <label ref={bagImgUploadRef} htmlFor="bag-img-upload" style={{ display: 'block', marginBottom: 20, borderRadius: 14, border: `1.5px dashed ${bagError === 'At least one photo is required.' ? '#c62828' : 'var(--border)'}`, background: 'var(--surface)', cursor: 'pointer', overflow: 'hidden', minHeight: 90, transition: 'border-color 0.15s' }}>
              {bagPreviews.length > 0
                ? <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: 12 }}>
                    {bagPreviews.map((p, i) => (
                      <div
                        key={i}
                        draggable
                        onDragStart={e => { e.stopPropagation(); setBagDragIndex(i) }}
                        onDragOver={e => { e.preventDefault() }}
                        onDrop={e => { e.preventDefault(); e.stopPropagation(); reorderBagImages(bagDragIndex, i); setBagDragIndex(null) }}
                        onDragEnd={() => setBagDragIndex(null)}
                        onClick={e => e.preventDefault()}
                        style={{ position: 'relative', opacity: bagDragIndex === i ? 0.4 : 1, cursor: 'grab' }}
                      >
                        <img src={p} alt="" style={{ width: 68, height: 68, borderRadius: 10, objectFit: 'cover', border: '1px solid #e8e5e0' }} />
                        <button
                          type="button"
                          onClick={e => { e.preventDefault(); e.stopPropagation(); removeBagImage(i) }}
                          style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', background: '#e00', color: '#fff', border: 'none', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >×</button>
                      </div>
                    ))}
                    <div style={{ width: 68, height: 68, borderRadius: 10, border: '1.5px dashed #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: 22 }}>+</div>
                  </div>
                : <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 90, gap: 6 }}>
                    <div style={{ fontSize: 24, lineHeight: 1 }}>&#128247;</div>
                    <div style={{ fontSize: 13, color: '#888', fontWeight: 500 }}>Tap to upload photos</div>
                    <div style={{ fontSize: 11, color: '#bbb' }}>JPG, PNG — multiple allowed, drag to reorder</div>
                  </div>
              }
              <input id="bag-img-upload" type="file" accept="image/*" multiple onChange={handleBagImages} style={{ display: 'none' }} />
            </label>

            <div style={{ background: 'var(--surface)', borderRadius: 14, border: '1px solid var(--border)', padding: '16px 16px 4px', marginBottom: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.9px', marginBottom: 14 }}>Bags preorder — quick entry</div>
              <div className="form-row">
                <label>Name</label>
                <textarea
                  value={bagName}
                  onChange={e => handleBagName(e.target.value)}
                  rows={3}
                  placeholder={'Hermès Birkin 30 Togo Gold HW Repaired\n2800\n3920'}
                  style={{ width: '100%', boxSizing: 'border-box', fontSize: 13, borderRadius: 8, resize: 'vertical', lineHeight: 1.6 }}
                  required
                />
              </div>
            </div>

            <div style={{ background: 'var(--surface)', borderRadius: 14, border: '1px solid var(--border)', padding: '16px 16px 4px', marginBottom: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.9px', marginBottom: 14 }}>Item details</div>

              <div className="form-row">
                <label>Category</label>
                <select value={bagCategory} onChange={e => setBagCategory(e.target.value)}>
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>

              <div className="form-row">
                <label>Model name</label>
                <input
                  type="text"
                  value={bagModel}
                  onChange={e => { setBagError(''); setBagModel(e.target.value) }}
                  placeholder="e.g. Birkin 30 Togo Gold HW Repaired"
                  required
                />
              </div>

              <div className="form-2col">
                <div className="form-row">
                  <label>Brand</label>
                  <select value={bagBrand} onChange={e => setBagBrand(e.target.value)}>
                    {BAG_FORM_BRANDS.map(b => <option key={b}>{b}</option>)}
                  </select>
                </div>
                <div className="form-row">
                  <label>Condition</label>
                  <select value={bagCondition} onChange={e => setBagCondition(e.target.value)}>
                    {BAG_CONDITIONS.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div style={{ background: 'var(--surface)', borderRadius: 14, border: '1px solid var(--border)', padding: '16px 16px 4px', marginBottom: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.9px', marginBottom: 14 }}>Pricing</div>
              <div className="form-row">
                <label>Cost price</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="number"
                    min="0"
                    value={bagCostPrice}
                    onChange={e => { setBagError(''); setBagCostPrice(e.target.value === '' ? '' : String(Math.max(0, Number(e.target.value)))) }}
                    placeholder="e.g. 28000"
                    style={{ flex: 1 }}
                    required={!bagSellingPrice}
                  />
                  <select value={bagCostCurrency} onChange={e => setBagCostCurrency(e.target.value)} style={{ width: 90 }}>
                    <option value="EUR">EUR</option>
                    <option value="CNY">CNY</option>
                  </select>
                </div>
                {bagCostCurrency === 'CNY' && bagCostPrice && (
                  <div style={{ fontSize: 12, color: '#b0a898', marginTop: 4 }}>
                    {(usdToCnyRate && rate) ? `≈ €${Math.round(Number(bagCostPrice) / usdToCnyRate / rate).toLocaleString()}` : 'Loading CNY rate…'}
                  </div>
                )}
              </div>

              <div className="form-row">
                <label>Selling price — optional</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="number"
                    min="0"
                    value={bagSellingPrice}
                    onChange={e => { setBagError(''); setBagSellingPrice(e.target.value === '' ? '' : String(Math.max(0, Number(e.target.value)))) }}
                    placeholder="leave blank to auto-calc as cost + 40%"
                    style={{ flex: 1 }}
                    required={!bagCostPrice}
                  />
                  <select value={bagSellingCurrency} onChange={e => setBagSellingCurrency(e.target.value)} style={{ width: 90 }}>
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                    <option value="CNY">CNY</option>
                  </select>
                </div>
                {(bagCostPrice || bagSellingPrice) && (() => {
                  const costEur = bagCostPrice
                    ? bagCostCurrency === 'CNY' ? Number(bagCostPrice) / (usdToCnyRate || 1) / (rate || 1) : Number(bagCostPrice)
                    : null
                  const sellingAmount = bagSellingPrice ? Number(bagSellingPrice) : costEur * 1.4
                  const includeCny = bagIsPreorder || bagSellingCurrency === 'CNY'
                  const converted = convertBagSellingPrices(sellingAmount, bagSellingCurrency, includeCny)
                  if (!converted) {
                    return <div style={{ fontSize: 12, color: '#b0a898', marginTop: 4 }}>Loading exchange rates…</div>
                  }
                  const cnyPreview = converted.priceCny ? ` ≈ ¥${Math.round(converted.priceCny).toLocaleString()}` : ''
                  return (
                    <div style={{ fontSize: 12, color: '#b0a898', marginTop: 4 }}>
                      Selling price: €{Math.round(converted.priceEur).toLocaleString()} ≈ ${Math.round(converted.priceUsd).toLocaleString()}{cnyPreview}{!bagSellingPrice ? ' (auto: cost + 40%)' : ''}
                    </div>
                  )
                })()}
              </div>
            </div>

            {/* Preorder toggle */}
            <div
              onClick={() => setBagIsPreorder(v => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: bagIsPreorder ? 'var(--gold-light)' : 'var(--surface)', borderRadius: 14, border: `1px solid ${bagIsPreorder ? 'rgba(184,150,106,0.4)' : 'var(--border)'}`, marginBottom: 20, cursor: 'pointer', transition: 'all 0.2s' }}
            >
              <div style={{ width: 44, height: 26, borderRadius: 13, background: bagIsPreorder ? '#b8965a' : '#ddd', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                <div style={{ position: 'absolute', top: 3, left: bagIsPreorder ? 21 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.18s', boxShadow: '0 1px 4px rgba(0,0,0,0.18)' }} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: bagIsPreorder ? 'var(--gold)' : 'var(--text)' }}>Preorder</div>
                <div style={{ fontSize: 11, color: '#b0a898', marginTop: 1 }}>{bagIsPreorder ? 'No SKU required — item not yet in stock' : 'Toggle on if item is not yet in stock'}</div>
              </div>
            </div>

            <button type="submit" className="btn btn-dark btn-full" disabled={bagPosting} style={{ height: 48, fontSize: 15, borderRadius: 12 }}>
              {bagPosting ? <span className="spinner" style={{ width: 18, height: 18 }} /> : bagIsPreorder ? 'Post bags preorder' : 'Post to catalog'}
            </button>
          </form>
        </div>
      )}
      {tab === 'clients' && (
        <div style={{ padding: 16, maxWidth: 540 }}>
          {/* Generate link */}
          <div className="card" style={{ padding: '20px', marginBottom: 20 }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Generate invite link</div>
            {linkError && <div className="error-msg" style={{ marginBottom: 10 }}>{linkError}</div>}
            {generatedLink && (
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ fontSize: 12, color: 'var(--muted)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{generatedLink}</div>
                <button onClick={() => handleCopy(generatedLink, 'new')} className="btn btn-sm" style={{ flexShrink: 0, fontSize: 12 }}>
                  {copied === 'new' ? '✓ Copied' : 'Copy'}
                </button>
              </div>
            )}
            <button className="btn btn-dark" onClick={handleGenerateLink} disabled={generating}>
              {generating ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '+ Generate new link'}
            </button>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 10 }}>Each link can only be used once. Share it directly with the dealer.</div>
          </div>

          {/* Pending links */}
          {!clientsLoading && pendingTokens.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Pending links ({pendingTokens.length})</div>
              {pendingTokens.map(t => {
                const link = `${window.location.origin}/join/${t.token}`
                return (
                  <div key={t.id} className="card" style={{ padding: '12px 14px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ fontSize: 12, color: 'var(--muted)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{link}</div>
                    <button onClick={() => handleCopy(link, t.id)} className="btn btn-sm" style={{ fontSize: 11, flexShrink: 0 }}>
                      {copied === t.id ? '✓' : 'Copy'}
                    </button>
                    <button onClick={() => handleRevokeToken(t.id)} style={{ background: 'none', border: 'none', color: '#d9534f', cursor: 'pointer', fontSize: 16, lineHeight: 1, flexShrink: 0, padding: '0 2px' }} title="Revoke">×</button>
                  </div>
                )
              })}
            </div>
          )}

          {/* Joined dealers */}
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
            {clientsLoading ? 'Loading…' : `Joined dealers (${clients.length})`}
          </div>
          {!clientsLoading && clients.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--muted)', textAlign: 'center', padding: 24 }}>No dealers have joined yet.</div>
          )}
          {clients.map(c => (
            <div key={c.id} className="card" style={{ padding: '14px 16px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#b8965a22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, color: '#b8965a', flexShrink: 0 }}>
                {(c.full_name || c.email || '?')[0].toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{c.full_name || '—'}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>{c.email}</div>
                {c.phone && <div style={{ fontSize: 12, color: 'var(--muted)' }}>{c.phone}</div>}
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', flexShrink: 0 }}>
                {new Date(c.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'supplier' && (
        <div style={{ padding: '0 16px 40px' }}>
          {supplierListings.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, color: 'var(--faint)', fontSize: 14 }}>No pending supplier submissions.</div>
          ) : supplierListings.map(listing => {
            const imgs = (listing.supplier_listing_images || []).sort((a, b) => a.position - b.position)
            const rd = supReviewData[listing.id] || {}
            const setRd = patch => setSupReviewData(prev => ({ ...prev, [listing.id]: { ...prev[listing.id], ...patch } }))
            return (
              <div key={listing.id} style={{ background: 'var(--surface)', border: '1px solid var(--border-light)', borderRadius: 14, padding: 16, marginBottom: 16 }}>
                {imgs.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                    {imgs.slice(0, 5).map((img, i) => (
                      <img
                        key={i} src={img.url} alt=""
                        onClick={() => openLb(imgs.map(x => x.url), i)}
                        style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border-light)', cursor: 'zoom-in', transition: 'opacity 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
                        onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                      />
                    ))}
                    {imgs.length > 5 && (
                      <div
                        onClick={() => openLb(imgs.map(x => x.url), 5)}
                        style={{ width: 64, height: 64, borderRadius: 8, background: 'var(--border-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--faint)', cursor: 'zoom-in' }}
                      >
                        +{imgs.length - 5}
                      </div>
                    )}
                  </div>
                )}

                {supEditId === listing.id ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <input className="input" placeholder={listing.category === 'Jewellery' ? 'Brand / Designer' : 'Brand'} value={supEditForm.brand} onChange={e => setSupEditForm(f => ({ ...f, brand: e.target.value }))} style={{ marginBottom: 0 }} />
                      <input className="input" placeholder={listing.category === 'Jewellery' ? 'Description' : 'Model'} value={supEditForm.model} onChange={e => setSupEditForm(f => ({ ...f, model: e.target.value }))} style={{ marginBottom: 0 }} />
                    </div>
                    {listing.category === 'Jewellery' ? (
                      <>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                          <select className="input" value={supEditForm.jewellery_type} onChange={e => setSupEditForm(f => ({ ...f, jewellery_type: e.target.value }))} style={{ marginBottom: 0 }}>
                            <option value="">Type</option>
                            {['Ring','Necklace','Bracelet','Earrings','Pendant','Brooch','Bangle','Set','Other'].map(t => <option key={t}>{t}</option>)}
                          </select>
                          <select className="input" value={supEditForm.metal} onChange={e => setSupEditForm(f => ({ ...f, metal: e.target.value }))} style={{ marginBottom: 0 }}>
                            <option value="">Metal</option>
                            {['18k Yellow Gold','18k White Gold','18k Rose Gold','Platinum','Sterling Silver','Vermeil','Gold (other)','Other'].map(m => <option key={m}>{m}</option>)}
                          </select>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                          <select className="input" value={supEditForm.stone} onChange={e => setSupEditForm(f => ({ ...f, stone: e.target.value }))} style={{ marginBottom: 0 }}>
                            <option value="">Stone</option>
                            {['Diamond','Ruby','Sapphire','Emerald','Pearl','Amethyst','Moissanite','No Stone','Other'].map(s => <option key={s}>{s}</option>)}
                          </select>
                          <input className="input" placeholder="Size / Measurements" value={supEditForm.size_info} onChange={e => setSupEditForm(f => ({ ...f, size_info: e.target.value }))} style={{ marginBottom: 0 }} />
                        </div>
                      </>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <input className="input" placeholder="Reference" value={supEditForm.reference} onChange={e => setSupEditForm(f => ({ ...f, reference: e.target.value }))} style={{ marginBottom: 0 }} />
                        <select className="input" value={supEditForm.scope_of_delivery} onChange={e => setSupEditForm(f => ({ ...f, scope_of_delivery: e.target.value }))} style={{ marginBottom: 0 }}>
                          <option value="">Scope</option>
                          <option>Watch Only</option>
                          <option>With Card</option>
                          <option>With Box</option>
                          <option>Card &amp; Box</option>
                        </select>
                      </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <select className="input" value={supEditForm.condition} onChange={e => setSupEditForm(f => ({ ...f, condition: e.target.value }))} style={{ marginBottom: 0 }}>
                        <option value="">Condition</option>
                        {CONDITIONS.map(c => <option key={c}>{c}</option>)}
                      </select>
                      <input className="input" placeholder="Asking price" type="number" value={supEditForm.asking_price} onChange={e => setSupEditForm(f => ({ ...f, asking_price: e.target.value }))} style={{ marginBottom: 0 }} />
                    </div>
                    <div style={{ position: 'relative' }}>
                      <textarea
                        className="input"
                        placeholder="Notes"
                        rows={2}
                        value={translatingNotes ? 'Translating…' : supEditForm.notes}
                        onChange={e => setSupEditForm(f => ({ ...f, notes: e.target.value }))}
                        disabled={translatingNotes}
                        style={{ marginBottom: 0, resize: 'vertical', width: '100%', boxSizing: 'border-box', opacity: translatingNotes ? 0.5 : 1 }}
                      />
                      {!translatingNotes && supEditForm.notes && /[一-鿿㐀-䶿]/.test(supEditForm.notes) && (
                        <button
                          onClick={() => {
                            setTranslatingNotes(true)
                            translateText(supEditForm.notes).then(t => {
                              setSupEditForm(f => ({ ...f, notes: t }))
                              setTranslatingNotes(false)
                            })
                          }}
                          style={{ position: 'absolute', top: 6, right: 6, fontSize: 10, padding: '2px 7px', borderRadius: 5, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', color: '#6b7280' }}
                        >
                          🌐 Translate
                        </button>
                      )}
                    </div>

                    {/* Image management */}
                    <div>
                      <div style={{ fontSize: 12, color: 'var(--faint)', marginBottom: 6 }}>Photos</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                        {supEditExistingImgs.filter(img => !supEditRemovedIds.includes(img.id)).map(img => (
                          <div key={img.id} style={{ position: 'relative' }}>
                            <img src={img.url} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border-light)' }} />
                            <button onClick={() => setSupEditRemovedIds(ids => [...ids, img.id])} style={{ position: 'absolute', top: 3, right: 3, background: 'rgba(0,0,0,0.65)', color: '#fff', border: 'none', borderRadius: '50%', width: 18, height: 18, fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                          </div>
                        ))}
                        {supEditNewPreviews.map((src, i) => (
                          <div key={i} style={{ position: 'relative' }}>
                            <img src={src} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '2px dashed var(--border-light)', opacity: 0.9 }} />
                            <button onClick={() => { setSupEditNewFiles(f => f.filter((_, j) => j !== i)); setSupEditNewPreviews(p => p.filter((_, j) => j !== i)) }} style={{ position: 'absolute', top: 3, right: 3, background: 'rgba(0,0,0,0.65)', color: '#fff', border: 'none', borderRadius: '50%', width: 18, height: 18, fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                          </div>
                        ))}
                        <label style={{ width: 72, height: 72, borderRadius: 8, border: '2px dashed var(--border-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--faint)', fontSize: 22 }}>
                          +
                          <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => {
                            const files = Array.from(e.target.files)
                            setSupEditNewFiles(f => [...f, ...files])
                            setSupEditNewPreviews(p => [...p, ...files.map(f => URL.createObjectURL(f))])
                          }} />
                        </label>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-dark btn-sm" onClick={() => saveSupplierListingEdit(listing.id)}>Save changes</button>
                      <button className="btn btn-sm" onClick={() => setSupEditId(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 2 }}>
                          {listing.category === 'Jewellery'
                            ? [listing.brand, listing.jewellery_type].filter(Boolean).join(' · ') || 'Jewellery Item'
                            : `${listing.brand || ''} ${listing.model || ''}`.trim()}
                        </div>
                        {listing.category === 'Jewellery' && (
                          <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600, background: '#ede9fe', color: '#7c3aed', marginBottom: 4 }}>Jewellery</span>
                        )}
                      </div>
                      <button className="btn btn-sm" style={{ fontSize: 11 }} onClick={() => enterSupEdit(listing)}>Edit</button>
                    </div>
                    {listing.category === 'Jewellery' ? (
                      <>
                        {listing.metal && <div style={{ fontSize: 12, color: 'var(--faint)', marginBottom: 2 }}>{listing.metal}{listing.stone ? ` · ${listing.stone}` : ''}</div>}
                        {listing.size_info && <div style={{ fontSize: 12, color: 'var(--faint)', marginBottom: 2 }}>Size: {listing.size_info}</div>}
                        {listing.model && <div style={{ fontSize: 12, color: 'var(--faint)', marginBottom: 2 }}>{listing.model}</div>}
                      </>
                    ) : (
                      <>
                        {listing.reference && <div style={{ fontSize: 12, color: 'var(--faint)', marginBottom: 2 }}>Ref. {listing.reference}</div>}
                        {listing.scope_of_delivery && <div style={{ fontSize: 12, color: 'var(--faint)', marginBottom: 2 }}>{listing.scope_of_delivery}</div>}
                      </>
                    )}
                    <div style={{ fontSize: 12, color: 'var(--faint)', marginBottom: 2 }}>{listing.condition}</div>
                    {listing.asking_price && (() => {
                      const cur = listing.asking_price_currency || 'CNY'
                      const sym = { EUR: '€', USD: '$', CNY: '¥', HKD: 'HK$' }[cur] || ''
                      const eurEq = cur === 'CNY' && usdToCnyRate && rate
                        ? ` (≈ €${Math.round(listing.asking_price / usdToCnyRate / rate).toLocaleString()})`
                        : cur === 'HKD' && hkdToEurRate
                        ? ` (≈ €${Math.round(listing.asking_price * hkdToEurRate).toLocaleString()})`
                        : cur === 'USD' && rate
                        ? ` (≈ €${Math.round(listing.asking_price / rate).toLocaleString()})`
                        : ''
                      return (
                        <div style={{ fontSize: 12, color: 'var(--faint)', marginBottom: 2 }}>
                          Asking: <strong>{sym}{Number(listing.asking_price).toLocaleString()}</strong>
                          {eurEq && <span style={{ color: '#9ca3af' }}>{eurEq}</span>}
                        </div>
                      )
                    })()}
                    {listing.notes && <div style={{ fontSize: 12, color: 'var(--faint)', marginBottom: 2 }}>{listing.notes}</div>}
                    <div style={{ fontSize: 11, color: 'var(--faint)', marginTop: 6 }}>
                      From: {listing.profiles?.full_name || '—'}{listing.profiles?.phone ? ` · ${listing.profiles.phone}` : ''}
                      {' · '}{new Date(listing.created_at).toLocaleDateString()}
                    </div>
                  </div>
                )}

                <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: 14, display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  {/* Selling price + Approve */}
                  <div style={{ flexShrink: 0 }}>
                    <div style={{ fontSize: 11, color: 'var(--faint)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                      Selling price
                      {(() => {
                        const ac = listing.asking_price_currency
                        const eurHint = ac === 'CNY' && usdToCnyRate && rate ? Math.round(listing.asking_price / usdToCnyRate / rate)
                          : ac === 'HKD' && hkdToEurRate ? Math.round(listing.asking_price * hkdToEurRate)
                          : null
                        return listing.asking_price && eurHint ? (
                          <button
                            onClick={() => setRd({ price: String(eurHint), currency: 'EUR' })}
                            style={{ fontSize: 11, color: '#b8965a', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' }}
                          >
                            Use ≈ €{eurHint.toLocaleString()}
                          </button>
                        ) : null
                      })()}
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <select
                        value={rd.currency || 'EUR'}
                        onChange={e => setRd({ currency: e.target.value })}
                        className="input"
                        style={{ width: 80, marginBottom: 0, fontSize: 13, height: 36 }}
                      >
                        <option value="EUR">€ EUR</option>
                        <option value="USD">$ USD</option>
                        <option value="CNY">¥ CNY</option>
                        <option value="HKD">HK$ HKD</option>
                      </select>
                      <input
                        className="input"
                        type="number"
                        placeholder="0"
                        value={rd.price || ''}
                        onChange={e => setRd({ price: e.target.value })}
                        style={{ width: 110, marginBottom: 0, fontWeight: 600, fontSize: 15, height: 36 }}
                      />
                      <button className="btn btn-dark btn-sm" onClick={() => approveSupplierListing(listing)} style={{ height: 36, whiteSpace: 'nowrap' }}>
                        ✓ Approve & Publish
                      </button>
                    </div>
                  </div>

                  <div style={{ width: 1, height: 36, background: 'var(--border-light)', flexShrink: 0 }} />

                  {/* Rejection reason + Reject */}
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ fontSize: 11, color: 'var(--faint)', marginBottom: 6 }}>Rejection reason <span style={{ color: '#9ca3af' }}>(optional)</span></div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input
                        className="input"
                        placeholder="Add a reason for rejection..."
                        value={rd.reason || ''}
                        onChange={e => setRd({ reason: e.target.value })}
                        style={{ flex: 1, marginBottom: 0, height: 36 }}
                      />
                      <button className="btn btn-danger btn-sm" onClick={() => rejectSupplierListing(listing)} style={{ height: 36, whiteSpace: 'nowrap' }}>
                        ✕ Reject
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {tab === 'rates' && (
        <div style={{ padding: '28px 28px 40px', maxWidth: 540 }}>
          <div style={{ marginBottom: 24 }}>
            <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700 }}>Exchange Rates</h2>
            <div style={{ fontSize: 13, color: 'var(--faint)' }}>Set today's rates — used instantly for supplier price conversions</div>
          </div>

          {rateMsg && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 14px', marginBottom: 20, fontSize: 13, color: '#15803d' }}>
              ✓ {rateMsg}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {RATE_PAIRS.map(({ from, to, label, desc }) => {
              const key = `${from}-${to}`
              const current = currentRatesData[key]
              const isSaving = savingRate === key
              return (
                <div key={key} style={{ background: 'var(--surface)', border: '1px solid var(--border-light)', borderRadius: 14, padding: '16px 18px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 16, letterSpacing: '-0.01em' }}>{label}</div>
                      <div style={{ fontSize: 12, color: 'var(--faint)', marginTop: 2 }}>{desc}</div>
                      {current && (
                        <div style={{ fontSize: 11, color: 'var(--faint)', marginTop: 4 }}>
                          Last set: {new Date(current.fetched_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </div>
                      )}
                    </div>
                    {current && (
                      <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 16 }}>
                        <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{Number(current.rate).toFixed(4)}</div>
                        <div style={{ fontSize: 11, color: 'var(--faint)' }}>current</div>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="number"
                      step="0.0001"
                      placeholder={from === 'USD' && to === 'CNY' ? 'e.g. 7.25' : from === 'HKD' ? 'e.g. 0.1175' : 'e.g. 1.085'}
                      value={rateInputs[key] || ''}
                      onChange={e => setRateInputs(r => ({ ...r, [key]: e.target.value }))}
                      style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border-light)', fontSize: 14, background: 'var(--surface2)', color: 'var(--text)' }}
                    />
                    <button
                      className="btn btn-dark"
                      onClick={() => handleSaveRate(from, to, rateInputs[key])}
                      disabled={isSaving || !rateInputs[key]}
                      style={{ flexShrink: 0, padding: '8px 20px' }}
                    >
                      {isSaving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Save'}
                    </button>
                  </div>
                </div>
              )
            })}
          </div>

          <div style={{ marginTop: 16, padding: '12px 14px', background: 'var(--surface2)', borderRadius: 10, fontSize: 12, color: 'var(--faint)', lineHeight: 1.6 }}>
            Saved rates take effect immediately in price displays and supplier approval calculations.
          </div>
        </div>
      )}

        {/* Invite User — agent + admin */}
        {tab === 'invite' && ['agent', 'admin'].includes(profile?.role) && (
          <div style={{ maxWidth: 480, paddingTop: 12 }}>
            <h2 style={{ margin: '0 0 24px', fontSize: 20, fontWeight: 700 }}>Invite User</h2>
            {inviteMsg && <div className="success-msg" style={{ marginBottom: 14 }}>{inviteMsg}</div>}
            {inviteError && <div className="error-msg" style={{ marginBottom: 14 }}>{inviteError}</div>}
            <form onSubmit={handleInvite} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--faint)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Full name</label>
                <input type="text" value={inviteName} onChange={e => setInviteName(e.target.value)} placeholder="Jean Michel" style={{ width: '100%', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--faint)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Email address</label>
                <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="dealer@company.com" required style={{ width: '100%', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--faint)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Role</label>
                <select value={inviteRole} onChange={e => setInviteRole(e.target.value)} style={{ width: '100%' }}>
                  <option value="dealer">Dealer — browse catalog, reserve items</option>
                  <option value="supplier">Watch Supplier — submit watch listings for review</option>
                  <option value="jewellery_supplier">Jewellery Supplier — submit jewellery listings</option>
                </select>
              </div>
              <button type="submit" className="btn btn-dark btn-full" disabled={inviting} style={{ marginTop: 4 }}>
                {inviting ? <span className="spinner" style={{ width: 16, height: 16 }} /> : 'Send invitation'}
              </button>
            </form>
            <div style={{ marginTop: 16, padding: '12px 14px', background: 'var(--surface2)', borderRadius: 10, fontSize: 12, color: 'var(--faint)', lineHeight: 1.6 }}>
              They'll receive an email with a link to set their password and access Brandville Vault immediately.
            </div>
          </div>
        )}

        <div style={{ flex: 1 }} />
        <Footer />
        </div> {/* end main scroll area */}
      </div> {/* end flex row */}
    </div>
  )
}
